// Centralized API data service with persistent caching and request optimization
// Usage:
//   import { apiGet, setAuthToken, clearCache, trackEvent } from './dataService.js'
//   const data = await apiGet('https://api.example.com/specs/123', { token })

import { getAccessToken, isDevelopment } from '../services/authService.js';
import { clearIndexedDBCache, getIndexedDBCache, setIndexedDBCache } from '../utils/indexedDBCache.js';
import { completePhase, startPhase, updatePhaseProgress } from '../utils/loadingService.js';
import { allCells } from '../state/globalSignals.js';
import { gatedFieldsToCellShapeKeys } from './explorerCellShape.js';
import { isDemoCatalogUnlocked, utcDayKey } from '../state/demoLimitStore.js';

const _cache = new Map(); // key: url, value: { ts, data }
const _inflight = new Map(); // key: cacheKey, value: Promise (request deduplication)
let _defaultToken = null;
let _isHydrated = false; // Track if we've loaded from IndexedDB

// Default cache durations (in ms) - OPTIMIZED FOR BACKEND CACHING
const CACHE_DURATION = {
  PRODUCT_LIST: 7 * 24 * 60 * 60 * 1000,  // 7 DAYS for product lists (backend-cached)
  CELL_DATA: 24 * 60 * 60 * 1000,          // 24 HOURS for individual cell data (backend-cached)
  FILTER_METADATA: 7 * 24 * 60 * 60 * 1000, // 7 DAYS for filter metadata
  DEFAULT: 60 * 60 * 1000                   // 1 hour for other requests
};

// ✅ CACHE ENABLED - IndexedDB + Backend Caching!
const DISABLE_CACHE = false; // Cache ist AN!

export function setAuthToken(token) { _defaultToken = token || null; }

/**
 * Thrown when an HTTP response is "successful" but is not JSON — almost always
 * the SPA's own index.html served by the deploy fallback.
 */
export class ApiShapeError extends Error {
  constructor(message, { url, contentType } = {}) {
    super(message);
    this.name = 'ApiShapeError';
    this.code = 'API_SHAPE';
    this.url = url;
    this.contentType = contentType;
  }
}

/**
 * Guard a response before `await res.json()`.
 *
 * Every `frontend:build:*` ships an `.htaccess` whose catch-all rewrite sends
 * unknown paths to `index.html`. A missing or renamed API route therefore
 * answers **200 text/html** with the SPA shell, so `res.ok` does NOT prove the
 * body is JSON — `.json()` then dies with an opaque "Unexpected token '<'".
 *
 * A response carrying *no* content-type at all is tolerated: the fallback
 * always ships `text/html`, so an absent header means an exotic proxy (or a
 * test double), not the index.html fallback.
 *
 * @param {Response} res - the fetch response, not yet consumed
 * @param {string} [context] - URL or label named in the error message
 * @throws {ApiShapeError} when a content-type is present and is not JSON
 */
export function assertJsonResponse(res, context) {
  const contentType = res?.headers?.get?.('content-type') || '';
  if (!contentType) return;
  if (contentType.toLowerCase().includes('application/json')) return;

  const where = context || res?.url || 'the API';
  throw new ApiShapeError(
    `Expected JSON from ${where} but got "${contentType}" — the response looks like the SPA index.html fallback (missing or renamed API route).`,
    { url: where, contentType }
  );
}

/**
 * Read a Blob as a base64 data URL.
 *
 * Uses `onload`/`onerror` rather than `onloadend`: `onloadend` also fires on
 * failure, where `reader.result` is `null` — the old code resolved that null
 * and then read `.length` off it.
 *
 * @returns {Promise<string|null>} data URL, or null when the read failed
 */
function readBlobAsDataUrl(blob) {
  return new Promise((resolve) => {
    let reader;
    try {
      reader = new FileReader();
    } catch (e) {
      console.warn('[dataService] FileReader unavailable:', e);
      resolve(null);
      return;
    }
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : null);
    };
    reader.onerror = () => {
      console.warn('[dataService] FileReader failed to read blob:', reader.error);
      resolve(null);
    };
    reader.onabort = () => resolve(null);
    try {
      reader.readAsDataURL(blob);
    } catch (e) {
      console.warn('[dataService] FileReader.readAsDataURL threw:', e);
      resolve(null);
    }
  });
}

// ============================================================================
// PLAUSIBLE ANALYTICS TRACKING
// ============================================================================

/**
 * Track a custom event in Plausible Analytics.
 * DSGVO-konform, cookieless, fire-and-forget.
 *
 * @param {string} eventName - Name of the event (e.g., 'Cell Viewed', 'Plot Viewed')
 * @param {Object} props - Event properties (e.g., { cell_id: '123', cell_name: 'Samsung INR21700-50E' })
 *
 * Usage:
 *   trackEvent('Cell Viewed', { cell_id: '123', cell_name: 'Samsung INR21700-50E' });
 *   trackEvent('Plot Viewed', { plot_type: 'score_plot', section: 'benchmarking' });
 *   trackEvent('Section Changed', { section: 'mechanical', cell_id: '123' });
 */
export function trackEvent(eventName, props = {}) {
  try {
    if (typeof window.plausible === 'function') {
      window.plausible(eventName, { props });
      if (isDevelopment()) {
        console.log(`[Analytics] 📊 ${eventName}`, props);
      }
    }
  } catch (e) {
    // Silently fail - analytics should never break the app
    if (isDevelopment()) {
      console.warn('[Analytics] Tracking failed:', e);
    }
  }
}

/**
 * Track a cell view event.
 * @param {string} cellId - Product ID
 * @param {string} cellName - Cell display name
 * @param {string} viewMode - View mode ('grid', 'table', 'freeplot', 'detail')
 */
export function trackCellView(cellId, cellName, viewMode = 'detail') {
  trackEvent('Cell Viewed', {
    cell_id: cellId,
    cell_name: cellName?.substring(0, 100), // Truncate for Plausible
    view_mode: viewMode
  });
}

/**
 * Track a plot view event.
 * @param {string} plotType - Type of plot ('score_plot', 'my_plot', 'resistance', etc.)
 * @param {string} section - Page section ('benchmarking', 'operation', etc.)
 */
export function trackPlotView(plotType, section = '') {
  trackEvent('Plot Viewed', {
    plot_type: plotType,
    section: section
  });
}

/**
 * Track section navigation within a cell page.
 * @param {string} section - Section name ('factsheet', 'scores', 'operation', etc.)
 * @param {string} cellId - Product ID
 */
export function trackSectionChange(section, cellId = '') {
  trackEvent('Section Changed', {
    section: section,
    cell_id: cellId
  });
}

/**
 * Track compare cart actions.
 * @param {string} action - 'added' or 'removed'
 * @param {string} cellId - Product ID
 * @param {string} cellName - Cell display name
 */
export function trackCompareAction(action, cellId, cellName = '') {
  trackEvent(action === 'added' ? 'Compare Added' : 'Compare Removed', {
    cell_id: cellId,
    cell_name: cellName?.substring(0, 100)
  });
}

/**
 * Track search performed.
 * @param {string} query - Search query
 * @param {number} resultCount - Number of results
 */
export function trackSearch(query, resultCount = 0) {
  if (query && query.trim().length >= 2) {
    trackEvent('Search Performed', {
      query: query.substring(0, 50), // Truncate for Plausible
      result_count: resultCount
    });
  }
}

/**
 * Track filter applied.
 * @param {string} filterType - Type of filter (manufacturer, format, chemistry, etc.)
 * @param {string} value - Filter value
 */
export function trackFilter(filterType, value = '') {
  trackEvent('Filter Applied', {
    filter_type: filterType,
    value: value?.substring(0, 50)
  });
}

// ============================================================================
// CACHE MANAGEMENT
// ============================================================================

export async function clearCache() {
  _cache.clear();
  _inflight.clear();
  // Clear IndexedDB cache
  try {
    await clearIndexedDBCache();
    console.log('[dataService] ✓ IndexedDB cache cleared');
  } catch (e) {
    console.warn('[dataService] Could not clear IndexedDB cache:', e);
  }
}

// Force refresh specific cache entry (bypasses cache)
export async function refreshCache(url, options = {}) {
  console.log(`[dataService] 🔄 Force refreshing: ${url}`);
  return apiGet(url, { ...options, cache: false });
}

/**
 * Drop every cached (and in-flight) Explorer-list entry, keyed off any
 * cacheKey containing '/explorer/v2'. Called after `unlockCell()` so the
 * next Grid/Table/Plot load re-fetches the un-gated cell instead of serving
 * a stale (gated) snapshot from earlier today.
 */
export function invalidateExplorerCache() {
  for (const key of Array.from(_cache.keys())) {
    if (key.includes('/explorer/v2')) _cache.delete(key);
  }
  for (const key of Array.from(_inflight.keys())) {
    if (key.includes('/explorer/v2')) _inflight.delete(key);
  }
}

// Hydrate cache from IndexedDB on first use
async function hydrateCache() {
  if (_isHydrated) return;
  _isHydrated = true;

  // Skip cache hydration if disabled
  if (DISABLE_CACHE) {
    console.log('[dataService] ⚠️ Cache disabled for development');
    return;
  }

  console.log('[dataService] 💾 Cache enabled - 24h cells, 7d lists (Backend + IndexedDB)');
  console.log('[dataService] Loading from IndexedDB...');

  startPhase('CACHE_HYDRATION');

  try {
    const cacheData = await getIndexedDBCache('batemo_api_cache');
    const cacheManifest = await getIndexedDBCache('batemo_cache_manifest');

    updatePhaseProgress('CACHE_HYDRATION', 0.3);

    if (cacheData && cacheManifest) {
      const now = Date.now();
      let loadedCount = 0;
      let expiredCount = 0;

      const entries = Object.entries(cacheData);
      const totalEntries = entries.length;

      entries.forEach(([key, value], index) => {
        const meta = cacheManifest[key];
        if (meta && (now - meta.ts) < meta.maxAge) {
          _cache.set(key, { ts: meta.ts, data: value });
          loadedCount++;
        } else {
          expiredCount++;
        }

        // Emit progress every 10 entries
        if (index % 10 === 0 || index === totalEntries - 1) {
          const progress = 0.3 + (0.7 * (index + 1) / totalEntries);
          updatePhaseProgress('CACHE_HYDRATION', progress);
        }
      });

      const totalSize = JSON.stringify(cacheData).length;
      console.log(`[dataService] ✓ Cache hydrated from IndexedDB: ${loadedCount} entries loaded, ${expiredCount} expired (${(totalSize / 1024 / 1024).toFixed(1)}MB)`);
    } else {
      console.log('[dataService] No IndexedDB cache found - starting fresh');
      updatePhaseProgress('CACHE_HYDRATION', 1.0);
    }

    completePhase('CACHE_HYDRATION');
  } catch (e) {
    console.warn('[dataService] IndexedDB hydration failed:', e);
    completePhase('CACHE_HYDRATION');
  }
}

// Helper: Strip large arrays from cell data before caching
// NOTE: As of API v2, arrays are limited to ~100 data points, so this is much lighter
function stripLargeArrays(data, url) {
  // Only strip for cell detail endpoints (contain measurement arrays)
  if (!url.includes('/products/') || url.includes('/pictures')) {
    return data; // Keep product lists and images intact
  }

  // Clone data - with reduced array sizes, we can keep most data now
  const stripped = JSON.parse(JSON.stringify(data));

  // NOTE: Arrays are now ~100 points instead of 1000s, so we keep them!
  // Only remove truly redundant nested objects if they exist
  if (stripped.electrical) {
    // Remove nested energy/capacity objects if they exist (deprecated structure)
    if (stripped.electrical.energy && typeof stripped.electrical.energy === 'object') {
      delete stripped.electrical.energy.soc;
      delete stripped.electrical.energy.temperature;
      delete stripped.electrical.energy.energy;
      delete stripped.electrical.energy.power;
    }
    if (stripped.electrical.capacity && typeof stripped.electrical.capacity === 'object') {
      delete stripped.electrical.capacity.soc;
      delete stripped.electrical.capacity.temperature;
      delete stripped.electrical.capacity.capacity;
      delete stripped.electrical.capacity.current;
    }
  }

  // Keep all other arrays - they are small now (100 points max)
  // This includes: DCC, CHC, DCP, CHP, EIS, batch, aging arrays

  return stripped;
}

// Persist cache to IndexedDB (debounced)
let _persistTimeout = null;
async function persistCache() {
  // Skip persistence if cache disabled
  if (DISABLE_CACHE) return;

  clearTimeout(_persistTimeout);
  _persistTimeout = setTimeout(async () => {
    try {
      const cacheData = {};
      const cacheManifest = {};

      _cache.forEach((value, key) => {
        // Strip large arrays before saving
        cacheData[key] = stripLargeArrays(value.data, key);
        cacheManifest[key] = {
          ts: value.ts,
          maxAge: getMaxAgeForKey(key)
        };
      });

      // Save to IndexedDB instead of localStorage
      await setIndexedDBCache('batemo_api_cache', cacheData);
      await setIndexedDBCache('batemo_cache_manifest', cacheManifest);

      const totalSize = JSON.stringify(cacheData).length;
      console.log(`[dataService] ✓ Cache persisted to IndexedDB: ${_cache.size} entries (${(totalSize / 1024 / 1024).toFixed(1)}MB)`);
    } catch (e) {
      console.error('[dataService] IndexedDB persist failed:', e);
    }
  }, 1000); // Debounce 1 second
}

// Get appropriate maxAge based on URL pattern
function getMaxAgeForKey(key) {
  if (key.includes('/products/') && !key.includes('/pictures')) {
    return CACHE_DURATION.CELL_DATA;
  }
  if (key.includes('/products') || key.includes('/api/v1/products')) {
    return CACHE_DURATION.PRODUCT_LIST;
  }
  return CACHE_DURATION.DEFAULT;
}

function normalizeCellId(cellId) {
  if (!cellId) return cellId;
  try {
    const idMap = (window.BATEMO_API && window.BATEMO_API.idMap) || null;
    if (idMap && typeof idMap === 'object' && cellId in idMap) {
      return idMap[cellId];
    }
  } catch { }
  return cellId;
}

/**
 * Live-update the DEMO deep-dive meter from the per-request quota headers the
 * backend sets on every GET /products/{id} success (X-Demo-Views-Used /
 * -Limit / -Reset-At). Called on each cell-detail success so the always-on
 * meter counts up the instant a cell opens, instead of waiting for the slower
 * usage-summary round-trip (this was the dead-code bug: applyUsageHeaders was
 * never wired into the fetch path).
 *
 * Reads the header synchronously and only imports the store when a demo quota
 * header is actually present, so non-demo users and every other endpoint pay
 * nothing (cheap no-op). Never throws — on any failure the meter degrades to
 * the usage-summary snapshot / localStorage mirror.
 *
 * NB: for a cross-origin API (staging/prod: api.staging.batemo.com / api.batemo.com) the
 * backend MUST CORS-expose these headers (Access-Control-Expose-Headers),
 * otherwise res.headers.get() returns null and this stays a no-op.
 */
async function applyDemoQuotaHeaders(res) {
  try {
    if (!res || typeof res.headers?.get !== 'function') return;
    // Cheap presence check before importing the store module.
    if (res.headers.get('X-Demo-Views-Used') == null) return;
    const { applyUsageHeaders } = await import('../state/demoLimitStore.js');
    applyUsageHeaders(res.headers);
  } catch (_) {
    /* non-fatal — meter falls back to the usage-summary snapshot */
  }
}

/**
 * GET JSON with optional token (Authorization header or query param), timeout, caching, and extra query params
 * @param {string} url - full URL
 * @param {object} options
 * @param {string} [options.token] - token overrides default
 * @param {string} [options.tokenParam] - if set, token will be sent as query param with this name
 * @param {string} [options.headerName] - if set (and tokenParam not set), send token in this header
 * @param {string} [options.headerPrefix] - optional prefix for the header value (e.g., 'Bearer '); default none
 * @param {object} [options.query] - additional query params to append
 * @param {number} [options.timeout=15000] - ms (increased default)
 * @param {boolean} [options.cache=true] - enable persistent cache
 * @param {number} [options.maxAge] - cache max age in ms (auto-determined if not set)
 */
export async function apiGet(url, { token, tokenParam, headerName, headerPrefix, query, timeout = 15000, cache = true, maxAge } = {}) {
  if (!url) throw new Error('apiGet: url is required');

  // Hydrate cache from IndexedDB on first use
  await hydrateCache();

  const useToken = token ?? _defaultToken ?? getAccessToken();

  // build final URL with query params
  let finalUrl = url;
  try {
    const u = new URL(url, window.location.origin);
    if (query && typeof query === 'object') {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, String(v));
      }
    }
    if (useToken && tokenParam) {
      u.searchParams.set(tokenParam, useToken);
    }
    finalUrl = u.toString();
  } catch {
    // fallback: append query string manually
    const params = new URLSearchParams();
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
      }
    }
    if (useToken && tokenParam) params.set(tokenParam, useToken);
    const sep = url.includes('?') ? '&' : '?';
    const qs = params.toString();
    finalUrl = qs ? (url + sep + qs) : url;
  }

  // include finalUrl + tokenParam marker in cache key to avoid mixing variants
  const hdrKey = headerName ? `|hdr:${headerName}` : (useToken && !tokenParam ? `|hdr:Authorization` : '');
  // Demo users' Explorer list is day-scoped: a cell unlocked today must not be
  // served yesterday's (gated) cached snapshot, and a fresh UTC day must not
  // reuse a stale one either. Non-demo users and non-Explorer URLs are
  // unaffected — same cacheKey shape as before.
  const dayKey = (finalUrl.includes('/explorer/v2') && isDemoCatalogUnlocked())
    ? `|day:${utcDayKey()}`
    : '';
  const cacheKey = finalUrl + (tokenParam ? `|qp:${tokenParam}` : '') + hdrKey + dayKey;

  // Auto-determine maxAge if not provided
  const effectiveMaxAge = maxAge || getMaxAgeForKey(cacheKey);

  // Check cache first (skip if disabled)
  if (cache && !DISABLE_CACHE && _cache.has(cacheKey)) {
    const { ts, data } = _cache.get(cacheKey);
    const age = Date.now() - ts;

    if (age < effectiveMaxAge) {
      console.log(`[dataService] ✓ Cache hit: ${finalUrl} (${Math.round(age / 1000)}s old)`);
      return data;
    } else {
      // Stale-While-Revalidate: Return stale data, refresh in background
      const staleGracePeriod = 7 * 24 * 60 * 60 * 1000; // 7 days stale allowed
      if (age < effectiveMaxAge + staleGracePeriod) {
        console.log(`[dataService] ⚡ Stale cache hit: ${finalUrl} (${Math.round(age / 1000)}s old) - revalidating in background`);

        // Return stale data immediately
        const staleData = data;

        // Revalidate in background (don't await)
        setTimeout(() => {
          apiGet(url, { token, tokenParam, headerName, headerPrefix, query, timeout, cache: false, maxAge })
            .then(freshData => {
              _cache.set(cacheKey, { ts: Date.now(), data: freshData });
              persistCache();
              console.log(`[dataService] ✓ Background revalidation complete: ${finalUrl}`);
            })
            .catch(err => console.warn(`[dataService] Background revalidation failed: ${finalUrl}`, err));
        }, 100);

        return staleData;
      } else {
        console.log(`[dataService] ⚠ Cache too stale: ${finalUrl} (${Math.round(age / 1000 / 60 / 60 / 24)} days old)`);
        _cache.delete(cacheKey);
      }
    }
  }

  // Check if request is already in-flight (deduplication)
  if (_inflight.has(cacheKey)) {
    console.log(`[dataService] Waiting for in-flight request: ${finalUrl}`);
    return _inflight.get(cacheKey);
  }

  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeout);

  console.log(`[dataService] Fetching: ${finalUrl}`);

  // Create and store the in-flight promise
  const fetchPromise = (async () => {
    try {
      const headers = {
        'Accept': 'application/json'
      };

      // Add JWT token if available (for authenticated endpoints)
      const jwtToken = getAccessToken();
      if (jwtToken) {
        headers['Authorization'] = `Bearer ${jwtToken}`;
      }

      if (typeof window !== 'undefined' && !window.__AUTH_HEADER_LOGGED) {
        console.log('[dataService] Debug auth headers (first request):', headers);
        window.__AUTH_HEADER_LOGGED = true;
      }

      // Note: JWT token (Authorization: Bearer) is already set above for both dev and production
      const res = await fetch(finalUrl, {
        method: 'GET',
        headers,
        signal: controller.signal
      });
      clearTimeout(tid);

      if (!res.ok) {
        console.error(`[dataService] Fetch failed: HTTP ${res.status} for ${finalUrl}`);

        // Handle 401 Unauthorized - invalid/expired token
        if (res.status === 401) {
          console.error('[dataService] 🔒 Unauthorized - token invalid or expired');

          // Try to refresh token (only in production, not development)
          if (!isDevelopment()) {
            try {
              const { refreshAccessToken } = await import('../services/authService.js');
              await refreshAccessToken();

              // Retry with new token
              const newJwtToken = getAccessToken();
              if (newJwtToken) {
                const newHeaders = {
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${newJwtToken}`
                };

                // Override with custom header if specified (for development)
                if (useToken && !tokenParam && isDevelopment()) {
                  if (headerName) {
                    newHeaders[headerName] = headerPrefix ? `${headerPrefix}${useToken}` : useToken;
                  }
                }

                console.log('[dataService] 🔄 Retrying request with refreshed token');
                // The first controller's timeout was already disarmed by the
                // clearTimeout above (and firing it would abort nothing new),
                // so the retry gets its own controller and its own timeout —
                // otherwise a hanging retry never settles and every waiter on
                // the shared _inflight promise hangs with it.
                const retryController = new AbortController();
                const retryTid = setTimeout(() => retryController.abort(), timeout);
                let retryRes;
                try {
                  retryRes = await fetch(finalUrl, {
                    method: 'GET',
                    headers: newHeaders,
                    signal: retryController.signal
                  });
                } catch (retryErr) {
                  // A retry that times out or fails on the network is NOT a
                  // failed token refresh — the refresh already succeeded. Mark
                  // it so the catch below rejects instead of logging the user
                  // out over a transient request failure.
                  try { retryErr.retryFetchFailed = true; } catch (_) { /* frozen error */ }
                  throw retryErr;
                } finally {
                  clearTimeout(retryTid);
                }

                if (retryRes.ok) {
                  console.log('[dataService] ✓ Retry successful after token refresh');
                  assertJsonResponse(retryRes, finalUrl);
                  const retryJson = await retryRes.json();
                  await applyDemoQuotaHeaders(retryRes);

                  if (cache && !DISABLE_CACHE) {
                    _cache.set(cacheKey, { ts: Date.now(), data: retryJson });
                    persistCache();
                  }

                  return retryJson;
                } else {
                  console.error('[dataService] ✗ Retry failed after token refresh:', retryRes.status);
                }
              }
            } catch (refreshError) {
              // The retry itself failed (timeout / network) after a successful
              // refresh: reject the shared in-flight promise, do not log out.
              // (Same for a non-JSON retry body: that is a routing/deploy
              // problem, not an expired session.)
              if (refreshError && (refreshError.retryFetchFailed || refreshError.code === 'API_SHAPE')) {
                throw refreshError;
              }
              console.error('[dataService] Token refresh failed:', refreshError);
              // Clear invalid tokens and logout
              const { logout } = await import('../services/authService.js');
              logout();
              throw new Error('Session expired - please log in again');
            }
          } else {
            // In development, just log the error (dev token might be invalid)
            console.warn('[dataService] Dev token authentication failed - check window.BATEMO_API.cell.token');
          }
        }

        // Try to parse error response
        let errorMessage = `HTTP ${res.status}`;
        try {
          const errorData = await res.json();
          // Demo daily view-limit (Slice A): FastAPI wraps the payload in
          // `detail`. Preserve it as a structured error so the cell-detail
          // catch can open the conversion overlay instead of an error page.
          const detail = errorData?.detail;
          if (
            res.status === 429 &&
            detail && typeof detail === 'object' &&
            detail.code === 'demo_daily_view_limit'
          ) {
            const limitErr = new Error(detail.message || 'Daily cell-view limit reached');
            limitErr.code = 'DEMO_LIMIT';
            limitErr.status = 429;
            // Carry the raw backend detail too, so consumers can key on either
            // err.code === 'DEMO_LIMIT' or err.detail?.code === 'demo_daily_view_limit'.
            limitErr.detail = detail;
            limitErr.demoLimit = {
              viewed_count: detail.viewed_count,
              daily_limit: detail.daily_limit,
              reset_at: detail.reset_at,
            };
            throw limitErr;
          }
          if (detail && typeof detail === 'string') {
            errorMessage = detail;
          } else if (detail && typeof detail === 'object' && detail.message) {
            errorMessage = detail.message;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch (e) {
          // Re-throw our own structured demo-limit error (thrown inside the
          // try above) — it must not be swallowed as a "non-JSON response".
          if (e && e.code === 'DEMO_LIMIT') throw e;
          // Response is not JSON (e.g., HTML error page)
          try {
            const text = await res.text();
            console.warn('[dataService] Non-JSON error response:', text.substring(0, 200));
          } catch (_) { /* body already consumed */ }
          errorMessage = `HTTP ${res.status}`;
        }

        throw new Error(errorMessage);
      }

      assertJsonResponse(res, finalUrl);
      const json = await res.json();
      await applyDemoQuotaHeaders(res);
      console.log(`[dataService] ✓ Fetch successful: ${finalUrl}`, {
        size: JSON.stringify(json).length,
        cached: cache && !DISABLE_CACHE
      });

      if (cache && !DISABLE_CACHE) {
        _cache.set(cacheKey, { ts: Date.now(), data: json });
        // Persist to localStorage (debounced)
        persistCache();
      }

      return json;
    } catch (err) {
      clearTimeout(tid);
      console.error(`[dataService] ✗ Fetch error for ${finalUrl}:`, err);
      throw err;
    } finally {
      // Remove from in-flight map when done
      _inflight.delete(cacheKey);
    }
  })();

  _inflight.set(cacheKey, fetchPromise);
  return fetchPromise;
}

/**
 * Try multiple candidate shapes and normalize to [ [label, value], ... ]
 */
export function normalizeTilePairs(json, { rootKeys = ['mechTiles', 'tiles', 'data'] } = {}) {
  // 1) Array of {label,value}
  for (const k of rootKeys) {
    const arr = json?.[k];
    if (Array.isArray(arr)) {
      return arr.map(it => [String(it.label ?? ''), String(it.value ?? '')]);
    }
  }
  // 2) Nested object map
  const candidates = [json?.specs?.mech, json?.mech, json];
  for (const obj of candidates) {
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      const entries = Object.entries(obj);
      if (entries.length && entries.every(([_, v]) => typeof v !== 'object')) {
        return entries.map(([k, v]) => [String(k), String(v ?? '')]);
      }
    }
  }
  return [];
}

/**
 * Build a URL for a given cell id from a config object.
 * Supports:
 *  - { url: 'https://api/.../{id}', token }
 *  - { baseUrl: 'https://api/.../cells', token }
 *  - { url: 'https://api/.../cells/123', token }
 */
export function buildCellUrl(cellId, cfg) {
  if (!cfg) return null;
  const { url, baseUrl } = cfg;
  if (url) {
    if (url.includes('{id}')) return url.replace('{id}', encodeURIComponent(cellId));
    return url; // assume already points to resource
  }
  if (baseUrl) {
    return baseUrl.replace(/\/$/, '') + '/' + encodeURIComponent(cellId);
  }
  return null;
}

/**
 * Extract minimal Explorer data from product list (no API calls needed)
 * Returns only: id, name, manufacturer, format, origin
 */
export function extractExplorerData(productList) {
  if (!Array.isArray(productList)) return [];

  return productList.map(product => ({
    id: product.product_id || product.id,
    name: product.product_name || product.name || 'Unknown',
    manufacturer: product.manufacturer || 'Unknown',
    format: product.format || product.form_factor || null,
    origin: product.origin || null,
    // Picture URL will be loaded separately (lazy)
  }));
}

/**
 * Extract KPIs from product list WITH embedded KPI data (future API enhancement)
 * Expects API response: { products: [ { product_id, kpis: { W_C10_grav, P_peak_grav, ... } } ] }
 */
export function extractKPIsFromProductList(productList) {
  if (!Array.isArray(productList)) return {};

  const kpiMap = {};
  productList.forEach(product => {
    const id = product.product_id || product.id;
    if (!id) return;

    // If API provides embedded KPIs (future enhancement)
    if (product.kpis) {
      kpiMap[id] = {
        // Energy (SI units: J/kg, J/m³)
        W_C10_grav: nNum(product.kpis.W_C10_grav),
        W_C10_vol: nNum(product.kpis.W_C10_vol),

        // Power (SI units: W/kg, W/m³)
        P_peak_grav: nNum(product.kpis.P_peak_grav),
        P_peak_vol: nNum(product.kpis.P_peak_vol),

        // Quality (fractions: 0.01 = 1%)
        CAP_spread_std_rel: nNum(product.kpis.CAP_spread_std_rel),
        CAP_loss_rel: nNum(product.kpis.CAP_loss_rel)
      };
    }
  });

  return kpiMap;
}

/**
 * Strip large arrays from cell data (client-side filtering after API response)
 * Keeps only scalar values and small summary stats
 */
function stripArraysFromCellData(cellData) {
  if (!cellData || typeof cellData !== 'object') return cellData;

  // Clone without arrays
  const stripped = {
    general: cellData.general,
    mechanical: cellData.mechanical ? {
      ...cellData.mechanical,
      // Keep pole dimensions (small arrays of 2 values)
      r_pole: cellData.mechanical.r_pole,
      x_pole: cellData.mechanical.x_pole,
      y_pole: cellData.mechanical.y_pole,
      z_pole: cellData.mechanical.z_pole
    } : null,
    chemical: cellData.chemical,
    safety: cellData.safety,
    electrical: cellData.electrical ? {
      // Keep only scalar values
      V_mean: cellData.electrical.V_mean,
      V_min: cellData.electrical.V_min,
      V_max: cellData.electrical.V_max,
      C_N: cellData.electrical.C_N,
      C_C10: cellData.electrical.C_C10,
      C_C10_grav: cellData.electrical.C_C10_grav,
      C_C10_vol: cellData.electrical.C_C10_vol,
      W_C10: cellData.electrical.W_C10,
      W_C10_grav: cellData.electrical.W_C10_grav,
      W_C10_vol: cellData.electrical.W_C10_vol,
      W_peak: cellData.electrical.W_peak,
      P_peak: cellData.electrical.P_peak,
      P_peak_grav: cellData.electrical.P_peak_grav,
      P_peak_vol: cellData.electrical.P_peak_vol,
      P_cont: cellData.electrical.P_cont,
      I_peak: cellData.electrical.I_peak,
      I_peak_grav: cellData.electrical.I_peak_grav,
      I_peak_vol: cellData.electrical.I_peak_vol,
      I_cont: cellData.electrical.I_cont,
      I_C10: cellData.electrical.I_C10,
      P_C10: cellData.electrical.P_C10,
      R_dc_25: cellData.electrical.R_dc_25,
      R_ch_25: cellData.electrical.R_ch_25,
      R_ac_25: cellData.electrical.R_ac_25,
      R_EIS_25: cellData.electrical.R_EIS_25,
      R_pol_25: cellData.electrical.R_pol_25,
      eta_dc_25: cellData.electrical.eta_dc_25,
      eta_ch_25: cellData.electrical.eta_ch_25,
      P_dc_25: cellData.electrical.P_dc_25,
      P_ch_25: cellData.electrical.P_ch_25,
      I_dc_25: cellData.electrical.I_dc_25,
      I_ch_25: cellData.electrical.I_ch_25,
      t_max_dc_25: cellData.electrical.t_max_dc_25,
      t_max_ch_25: cellData.electrical.t_max_ch_25,
      C_rate_DCP: cellData.electrical.C_rate_DCP,
      C_rate_CHP: cellData.electrical.C_rate_CHP,
      // DCC Arrays (needed for Requirement Plot interpolation with domain support)
      T_amb_DCC: cellData.electrical.T_amb_DCC || [],
      P_DCC: cellData.electrical.P_DCC || [],
      W_DCC: cellData.electrical.W_DCC || [],
      I_DCC: cellData.electrical.I_DCC || [],
      C_DCC: cellData.electrical.C_DCC || []
      // Other arrays still removed: t_max_DCC, eta_DCC, DCC_Ivar_*, etc.
    } : null,
    thermal: cellData.thermal ? {
      // Keep only scalar values
      delta_T_dc_25: cellData.thermal.delta_T_dc_25,
      delta_T_ch_25: cellData.thermal.delta_T_ch_25,
      P_loss_dc_25: cellData.thermal.P_loss_dc_25,
      P_loss_ch_25: cellData.thermal.P_loss_ch_25,
      W_loss_dc_25: cellData.thermal.W_loss_dc_25,
      W_loss_ch_25: cellData.thermal.W_loss_ch_25
      // Arrays removed: T_max_CHC, T_max_DCC, P_loss_CHC, P_loss_DCC, etc.
    } : null,
    batch: cellData.batch ? {
      // Keep only summary stats (no arrays)
      CAP_spread_mean: cellData.batch.CAP_spread_mean,
      CAP_spread_std_abs: cellData.batch.CAP_spread_std_abs,
      CAP_spread_std_rel: cellData.batch.CAP_spread_std_rel,
      CAP_spread_min: cellData.batch.CAP_spread_min,
      CAP_spread_max: cellData.batch.CAP_spread_max,
      EIS_spread_mean: cellData.batch.EIS_spread_mean,
      EIS_spread_std_abs: cellData.batch.EIS_spread_std_abs,
      EIS_spread_std_rel: cellData.batch.EIS_spread_std_rel
      // Array removed: CAP_spread, EIS_spread
    } : null,
    aging: cellData.aging ? {
      // Keep only summary stats (no arrays)
      CAP_loss_rel: cellData.aging.CAP_loss_rel,
      CAP_loss: cellData.aging.CAP_loss,
      EIS_increase_rel: cellData.aging.EIS_increase_rel,
      EIS_increase: cellData.aging.EIS_increase,
      Qtp_standard: cellData.aging.Qtp_standard
      // Arrays removed: CAP_T, CAP_Qtp_0, CAP_Qtp_standard, EIS_T, etc.
    } : null
  };

  return stripped;
}

/**
 * Fetch minimal cell data for Explorer (parallel batch loading)
 * Only fetches: general + electrical KPIs (no arrays!)
 * @param {string[]} cellIds - Array of cell IDs to fetch
 * @param {object} options - Options
 * @param {number} options.concurrency - Max parallel requests (default: 10)
 * @param {boolean} options.lite - Use lite mode if API supports it (default: true)
 * @param {boolean} options.stripArrays - Strip arrays client-side after fetch (default: true)
 * @param {function} options.onProgress - Callback(loaded, total, cellData) for progressive updates
 * @returns {Promise<Object>} Map of cellId → minimal cell data
 */
export async function batchGetMinimalCellData(cellIds, { concurrency = 10, lite = true, stripArrays = true, onProgress } = {}) {
  const results = {};
  const errors = [];

  console.log(`[dataService] 🚀 Batch loading ${cellIds.length} cells (parallel, concurrency: ${concurrency}, stripArrays: ${stripArrays})`);
  const startTime = Date.now();

  let loaded = 0;
  let totalBytesBeforeStrip = 0;
  let totalBytesAfterStrip = 0;

  // Process in parallel batches
  for (let i = 0; i < cellIds.length; i += concurrency) {
    const batch = cellIds.slice(i, i + concurrency);

    // Parallel fetch within batch
    const batchResults = await Promise.allSettled(
      batch.map(async (cellId) => {
        try {
          // Use getCellNormalized for proper unit conversion (SI → UI units)
          let cellData = await getCellNormalized(cellId, { lite });

          // Client-side array stripping (if API doesn't support lite mode)
          if (stripArrays && cellData) {
            const beforeSize = JSON.stringify(cellData).length;
            cellData = stripArraysFromCellData(cellData);
            const afterSize = JSON.stringify(cellData).length;
            totalBytesBeforeStrip += beforeSize;
            totalBytesAfterStrip += afterSize;
          }

          // Extract only minimal data needed for Explorer
          // NOTE: cellData is ALREADY normalized (flat structure), not nested!
          const minimalCell = {
            id: cellId,
            name: cellData?.name || 'Unknown',
            manufacturer: cellData?.manufacturer || 'Unknown',
            format: cellData?.mechanical?.format || null,
            origin: cellData?.origin || null,
            country: cellData?.country || null,
            date: cellData?.date || null, // ISO date string from API (e.g., "2020-03-15")
            year: cellData?.year || null, // Year as number for filters

            // Chemistry (chemical is nested in normalized data)
            cathode_material: cellData?.chemical?.cathode || null,
            main_chemistry: cellData?.chemical?.main_chemistry || cellData?.chemical?.cathode || null,
            anode_material: cellData?.chemical?.anode || null,

            // Format group
            format_group: cellData?.mechanical?.format_group || cellData?.mechanical?.format || null,

            // Technology: Derive from technology field or cathode_material
            technology: (() => {
              // First try top-level technology field
              if (cellData?.technology) return cellData.technology;

              // Fallback: derive from cathode with detailed categorization
              const cathode = cellData?.chemical?.cathode;
              if (cathode) {
                const chem = cathode.toUpperCase();

                if (chem.includes('LTO') || chem.includes('TITANATE')) {
                  return 'Lithium Titanate (LTO)';
                } else if (chem.includes('LFP') || chem.includes('LIFEPO4') || chem.includes('LiFePO4')) {
                  return 'Lithium Iron Phosphate (LFP)';
                } else if (chem.includes('NMC')) {
                  return 'Lithium-Ion (NMC)';
                } else if (chem.includes('NCA')) {
                  return 'Lithium-Ion (NCA)';
                } else if (chem.includes('LCO') || chem.includes('LICOO2')) {
                  return 'Lithium-Ion (LCO)';
                } else if (chem.includes('LMO') || chem.includes('LIMN2O4')) {
                  return 'Lithium-Ion (LMO)';
                } else if (chem.includes('NA') || chem.includes('SODIUM')) {
                  return 'Sodium-Ion';
                }
              }

              // Default fallback
              return 'Lithium-Ion (Other)';
            })(),

            // Key electrical parameters for filtering - ALL THREE BASIS TYPES
            // Energy (W_C10) - absolute, gravimetric, volumetric
            W_C10: nNum(cellData?.electrical?.W_C10),
            E_grav_Wh_per_kg: jPerKgToWhPerKg(cellData?.electrical?.W_C10_grav),
            W_C10_vol: nNum(cellData?.electrical?.W_C10_vol),

            // Power (P_peak) - absolute, gravimetric, volumetric
            P_peak: nNum(cellData?.electrical?.P_peak),
            P_grav_W_per_kg: nNum(cellData?.electrical?.P_peak_grav),
            P_peak_vol: nNum(cellData?.electrical?.P_peak_vol),

            // Capacity (C_C10) - absolute, gravimetric, volumetric
            C_C10: nNum(cellData?.electrical?.C_C10),
            C_C10_grav: nNum(cellData?.electrical?.C_C10_grav),
            C_C10_vol: nNum(cellData?.electrical?.C_C10_vol),

            // Current (I_peak) - absolute, gravimetric, volumetric
            I_peak: nNum(cellData?.electrical?.I_peak),
            I_peak_grav: nNum(cellData?.electrical?.I_peak_grav),
            I_peak_vol: nNum(cellData?.electrical?.I_peak_vol),

            // Voltage
            V_nom: nNum(cellData?.electrical?.V_mean),
            V_min: nNum(cellData?.electrical?.V_min),
            V_max: nNum(cellData?.electrical?.V_max),

            // Quality metrics
            CAP_spread_std_rel: nNum(cellData?.batch?.CAP_spread_std_rel),
            CAP_loss_rel: nNum(cellData?.aging?.CAP_loss_rel),

            // Mechanical
            mass_g: nNum(cellData?.mechanical?.m_cell) != null ? cellData.mechanical.m_cell * 1000 : null,
            vol_cell_cm3: nNum(cellData?.mechanical?.vol_cell) != null ? cellData.mechanical.vol_cell * 1e6 : null
          };

          // DEBUG: Log first cell to verify data structure
          if (!window.__BATEMO_FIRST_CELL_LOGGED__) {
            console.log('[dataService] 🔍 First cell minimal data:', {
              id: minimalCell.id,
              name: minimalCell.name,
              manufacturer: minimalCell.manufacturer,
              country: minimalCell.country,
              date: minimalCell.date,
              year: minimalCell.year,
              technology: minimalCell.technology,
              main_chemistry: minimalCell.main_chemistry,
              format_group: minimalCell.format_group
            });
            console.log('[dataService] ✅ All top-level fields present:', {
              hasName: !!minimalCell.name,
              hasManufacturer: !!minimalCell.manufacturer,
              hasCountry: !!minimalCell.country,
              hasDate: !!minimalCell.date,
              hasYear: !!minimalCell.year
            });
            window.__BATEMO_FIRST_CELL_LOGGED__ = true;
          }

          return minimalCell;
        } catch (err) {
          console.warn(`[dataService] Failed to load cell ${cellId}:`, err);
          errors.push({ id: cellId, error: err });
          return {
            id: cellId,
            name: 'Error loading',
            manufacturer: 'Unknown',
            format: null,
            origin: null,
            country: null,
            kpis: {}
          };
        }
      })
    );

    // Collect results and update progress
    batchResults.forEach((result) => {
      if (result.status === 'fulfilled' && result.value) {
        const cell = result.value;
        results[cell.id] = cell;
        loaded++;

        // Progressive update callback
        if (onProgress) {
          onProgress(loaded, cellIds.length, cell);
        }
      }
    });

    console.log(`[dataService] Progress: ${loaded}/${cellIds.length} cells loaded`);
  }

  const duration = Date.now() - startTime;
  console.log(`[dataService] ✓ Batch complete: ${loaded}/${cellIds.length} successful in ${duration}ms (${(duration / loaded).toFixed(0)}ms/cell)`);

  if (stripArrays && totalBytesBeforeStrip > 0) {
    const reduction = ((1 - totalBytesAfterStrip / totalBytesBeforeStrip) * 100).toFixed(0);
    console.log(`[dataService] 📉 Array stripping: ${(totalBytesBeforeStrip / 1024).toFixed(0)}KB → ${(totalBytesAfterStrip / 1024).toFixed(0)}KB (${reduction}% reduction)`);
  }

  if (errors.length > 0) {
    console.warn(`[dataService] ⚠ ${errors.length} cells failed to load:`, errors);
  }

  return results;
}

/**
 * Fetch cell data with optional lite mode (excludes large arrays)
 * @param {string} cellId - Cell ID
 * @param {object} overrides - Config overrides
 * @param {boolean} overrides.lite - If true, excludes large measurement arrays (faster, smaller)
 * @param {string[]} overrides.categories - Array of categories to fetch (default: all)
 */
export async function getCellData(cellId, overrides = {}) {
  const cfg = overrides.config || (window.BATEMO_API && window.BATEMO_API.cell) || {};
  const resolvedId = normalizeCellId(cellId);
  const url = overrides.url || buildCellUrl(resolvedId, cfg);
  const token = overrides.token ?? cfg.token ?? null;
  const tokenParam = overrides.tokenParam ?? cfg.tokenParam ?? null;
  const headerName = overrides.headerName ?? cfg.headerName ?? null;
  const headerPrefix = overrides.headerPrefix ?? cfg.headerPrefix ?? null;

  // Build query with required categories parameter (API v2 breaking change)
  let query = overrides.query || cfg.query || {};

  // Categories are now REQUIRED - default to all categories
  const categories = overrides.categories || [
    'general', 'chemical', 'electrical', 'mechanical',
    'safety', 'thermal', 'aging', 'batch'
  ];
  query = { ...query, categories: categories.join(',') };

  if (overrides.lite === true) {
    query = { ...query, lite: '1' };
  }

  // DEMO metric-tiering for BROWSE surfaces (Compare — WS 1.4b): when a caller
  // passes `fieldGating: 'demo'`, append `field_gating=demo` so the backend
  // withholds premium scalars for external demo users (no-op for PRO/admin, and
  // for demo cells). Because the query param becomes part of the request URL,
  // the apiGet cache key AND in-flight dedup key (both = finalUrl + auth marker)
  // are AUTOMATICALLY distinct from the un-gated detail-page fetch — the gated
  // Compare variant and the exact single-cell detail variant can never poison
  // each other in IndexedDB or the in-memory cache. The detail PAGE (CellPage)
  // deliberately does NOT pass this, so it keeps its exact-within-quota view.
  if (overrides.fieldGating) {
    query = { ...query, field_gating: overrides.fieldGating };
  }

  if (!url) throw new Error('getCellData: no URL provided');
  return apiGet(url, { token, tokenParam, headerName, headerPrefix, query, timeout: overrides.timeout });
}

// ---- Normalization for your provided product schema (SI → UI units) ----
function nNum(x) {
  // Handle German number format (comma as decimal separator)
  if (typeof x === 'string') {
    x = x.replace(',', '.')
  }
  const n = Number(x)
  return Number.isFinite(n) ? n : null
}

/**
 * Robustly extract year from a date string or year value.
 * Parses the year directly from the string (e.g. "2020-03-15" → 2020)
 * instead of using new Date() which can produce wrong results with
 * non-standard date formats or timezone edge cases.
 */
function extractYear(dateStr, yearFallback) {
  // 1. Try parsing year from date string (most reliable)
  if (dateStr && typeof dateStr === 'string') {
    // Match leading 4-digit year from ISO-like formats: "2020-03-15", "2020-03-15T...", "2020/03/15", etc.
    const match = dateStr.match(/^(\d{4})/)
    if (match) {
      const y = parseInt(match[1], 10)
      if (y >= 1990 && y <= 2100) return y
    }
  }
  // 2. Fallback to explicit year field
  const y = nNum(yearFallback)
  if (y != null && y >= 1990 && y <= 2100) return Math.round(y)
  return null
}
const cToAh = (c) => { const n = nNum(c); return n == null ? null : (n / 3600); };
const jToWh = (j) => { const n = nNum(j); return n == null ? null : (n / 3600); };
const jPerKgToWhPerKg = (jkg) => { const n = nNum(jkg); return n == null ? null : (n / 3600); };
const jPerM3ToWhPerL = (jm3) => { const n = nNum(jm3); return n == null ? null : (n / 3.6e6); };
const aPerM3ToAPerL = (am3) => { const n = nNum(am3); return n == null ? null : (n / 1000); };
const wPerM3ToWPerL = (wm3) => { const n = nNum(wm3); return n == null ? null : (n / 1000); };
const ohmToMilliOhm = (r) => { const n = nNum(r); return n == null ? null : (n * 1000); };
const kToC = (k) => { const n = nNum(k); return n == null ? null : (n - 273.15); };

/**
 * Returns normalized cell data from API
 *
 * API STRUCTURE (API v2 with categories parameter):
 * New response: { product_id: "...", categories: { general: {...}, electrical: {...}, ... } }
 * Old response (backward compat): { general: {...}, electrical: {...}, ... }
 *
 * Categories:
 * - electrical: W_C10 (J), P_peak (W), C_C10 (coulomb), I_peak (A), W_C10_grav (J/kg), P_peak_grav (W/kg), etc.
 * - mechanical: m_cell (kg), vol_cell (m³), format, dimensions, etc.
 * - aging: CAP_loss_rel (fraction), EIS_increase_rel (fraction), CAP_loss (absolute As), EIS_increase (absolute Ohm)
 * - batch: CAP_spread_std_rel (fraction), EIS_spread_std_rel (fraction), CAP_spread (array), EIS_spread (array)
 * - chemical: main_chemistry, anode_material, cathode_material
 * - general: manufacturer, name, origin, country, technology
 *
 * This function ensures consistent field names and units across the app.
 */
export async function getCellNormalized(cellId, overrides = {}) {
  let rawResponse;
  try {
    rawResponse = await getCellData(cellId, overrides);
  } catch (err) {
    // CENTRAL DEMO-LIMIT INTERCEPTOR (Bug A): every surface that opens a cell
    // routes through getCellNormalized, so fire the ONE conversion overlay
    // (or slim toast on repeat) here, then re-throw. Callers treat a DEMO_LIMIT
    // rejection purely as "data unavailable" — never as display responsibility.
    if (err?.code === 'DEMO_LIMIT' || err?.detail?.code === 'demo_daily_view_limit') {
      try {
        const { openLimitModalOrToast } = await import('../state/demoLimitStore.js');
        openLimitModalOrToast(err.demoLimit || err.detail || {});
      } catch (_) { /* store unavailable — still re-throw below */ }
    }
    throw err;
  }
  console.log('[getCellNormalized] Raw API response:', rawResponse);

  // Handle new API v2 response structure: { product_id, categories: {...} }
  // AND backward compatibility with old structure: { general: {...}, electrical: {...} }
  const raw = rawResponse?.categories || rawResponse;

  if (rawResponse?.product_id) {
    console.log('[getCellNormalized] Using new API v2 response structure');
  }

  const C_nom_Ah = cToAh(raw?.electrical?.C_N);
  const C_c10_Ah = cToAh(raw?.electrical?.C_C10);
  const E_c10_Wh = jToWh(raw?.electrical?.W_C10);

  // Efficiencies (stored as fractions, need to convert to percent)
  const eta_dc_pct = (nNum(raw?.electrical?.eta_dc_25) ?? null);
  const eta_ch_pct = (nNum(raw?.electrical?.eta_ch_25) ?? null);
  const efficiency_pct = (eta_dc_pct != null && eta_ch_pct != null)
    ? (eta_dc_pct * eta_ch_pct * 100) // raw are fractions → product → percent
    : null;

  // Calculate continuous and standard (dc_25) power/current densities (if not provided by API)
  const mass_kg = nNum(raw?.mechanical?.m_cell); // mass in kg
  const vol_m3 = nNum(raw?.mechanical?.vol_cell); // volume in m³
  const P_cont = nNum(raw?.electrical?.P_cont);
  const I_cont = nNum(raw?.electrical?.I_cont);
  const P_dc_25 = nNum(raw?.electrical?.P_dc_25);
  const I_dc_25 = nNum(raw?.electrical?.I_dc_25);

  const P_cont_grav = (P_cont != null && mass_kg != null) ? P_cont / mass_kg : nNum(raw?.electrical?.P_cont_grav);
  const P_cont_vol = (P_cont != null && vol_m3 != null) ? P_cont / vol_m3 : nNum(raw?.electrical?.P_cont_vol);
  const I_cont_grav = (I_cont != null && mass_kg != null) ? I_cont / mass_kg : nNum(raw?.electrical?.I_cont_grav);
  const I_cont_vol = (I_cont != null && vol_m3 != null) ? I_cont / vol_m3 : nNum(raw?.electrical?.I_cont_vol);

  const P_dc_25_grav = (P_dc_25 != null && mass_kg != null) ? P_dc_25 / mass_kg : nNum(raw?.electrical?.P_dc_25_grav);
  const P_dc_25_vol = (P_dc_25 != null && vol_m3 != null) ? P_dc_25 / vol_m3 : nNum(raw?.electrical?.P_dc_25_vol);
  const I_dc_25_grav = (I_dc_25 != null && mass_kg != null) ? I_dc_25 / mass_kg : nNum(raw?.electrical?.I_dc_25_grav);
  const I_dc_25_vol = (I_dc_25 != null && vol_m3 != null) ? I_dc_25 / vol_m3 : nNum(raw?.electrical?.I_dc_25_vol);

  const result = {
    id: cellId || '',
    manufacturer: raw?.general?.manufacturer || '',
    name: raw?.general?.name || '',
    origin: raw?.general?.origin || '',
    country: raw?.general?.country || '',
    date: raw?.general?.date || null, // ISO date string (e.g., "2020-03-15")
    year: extractYear(raw?.general?.date, raw?.general?.year),
    isDemo: !!raw?.general?.is_demo,
    isHarvested: (raw?.general?.origin === 'harvested'),
    technology: raw?.general?.technology || 'Li-Ion', // Li-Ion, Na-Ion, etc.

    // NO `createdAt` HERE — DELIBERATE, and the reason matters.
    //
    // This function and `modules/explorerCellShape.js#transformExplorerItem`
    // are the two parallel conversion paths that must stay in sync (frontend
    // CLAUDE.md). The explorer one carries `createdAt` (catalogue-INSERTION
    // date) for the `addedDate` sort; this one CANNOT, because the per-cell
    // detail response has no such field: its payload is a dump of the
    // `product_data_model` categories (`general_*` columns), while
    // `Product.created_at` is a separate bolted-on column that only the
    // /explorer/v2 list serialiser (`products/functions.py`) emits. Mapping
    // `general.date` (the MANUFACTURER's release date) into `createdAt` to make
    // the shapes "match" would silently answer a different question.
    //
    // Consequence, handled rather than ignored: on MainAppPage's legacy
    // fallback load (which builds cells from this path) the `addedDate` sort has
    // no data, so `state/filterStore.js` hides the option and downgrades a
    // `?sort=addedDate` deep link visibly. To make the sort work on BOTH paths a
    // BACKEND change is required — expose the insertion date in the detail
    // response (a new `general.created_at`, or a top-level field, plus a bump of
    // the week-long product cache) — then add it here AND keep the explorer
    // transform's key name identical.

    // General object for components that need it (e.g., safety features availability)
    general: {
      manufacturer: raw?.general?.manufacturer || '',
      name: raw?.general?.name || '',
      origin: raw?.general?.origin || '',
      country: raw?.general?.country || '',
      date: raw?.general?.date || null,
      year: extractYear(raw?.general?.date, raw?.general?.year),
      is_demo: !!raw?.general?.is_demo,
      technology: raw?.general?.technology || 'Li-Ion',
      internal_analysis_available: raw?.general?.internal_analysis_available ?? true, // Default true for backward compatibility
      bid: raw?.general?.bid || '' // Internal Batemo ID
    },

    chemical: {
      anode: raw?.chemical?.anode_material || '',
      cathode: raw?.chemical?.cathode_material || '',
      housing: raw?.chemical?.housing_material || '', // steel, aluminum, polymer
      main_chemistry: raw?.chemical?.main_chemistry || '', // NCA, LTO, NMC, LFP, etc. (legacy)
      // NEW: Material group fields for filtering
      anode_material_group: raw?.chemical?.anode_material_group || '', // Graphite, Silicon, LTO, etc.
      cathode_material_group: raw?.chemical?.cathode_material_group || '', // NMC, NCA, LFP, etc.
      anode_material_cc: raw?.chemical?.anode_material_cc || '', // Current collector anode (e.g., copper)
      cathode_material_cc: raw?.chemical?.cathode_material_cc || '' // Current collector cathode (e.g., aluminum)
    },

    electrical: {
      V_mean: nNum(raw?.electrical?.V_mean),
      V_min: nNum(raw?.electrical?.V_min),
      V_max: nNum(raw?.electrical?.V_max),

      C_nom_Ah: C_nom_Ah,
      C_c10_Ah: C_c10_Ah,
      C_grav_Ah_per_kg: cToAh(raw?.electrical?.C_C10_grav), // C/10 gravimetric capacity
      C_vol_Ah_per_L: cToAh(raw?.electrical?.C_C10_vol) != null ? cToAh(raw?.electrical?.C_C10_vol) / 1000 : null, // C/10 volumetric (C/m³ → Ah/L)

      E_c10_Wh: E_c10_Wh,
      E_grav_Wh_per_kg: jPerKgToWhPerKg(raw?.electrical?.W_C10_grav), // C/10 gravimetric energy
      E_vol_Wh_per_L: jPerM3ToWhPerL(raw?.electrical?.W_C10_vol),   // C/10 volumetric energy

      // C/10 discharge specific (for KPI tiles) - SI units
      W_C10: nNum(raw?.electrical?.W_C10),           // J (absolute)
      W_C10_grav: nNum(raw?.electrical?.W_C10_grav), // J/kg (gravimetric)
      W_C10_vol: nNum(raw?.electrical?.W_C10_vol),   // J/m³ (volumetric)

      C_C10: nNum(raw?.electrical?.C_C10),           // C (coulomb, absolute)
      C_C10_grav: nNum(raw?.electrical?.C_C10_grav), // C/kg (gravimetric)
      C_C10_vol: nNum(raw?.electrical?.C_C10_vol),   // C/m³ (volumetric)

      W_peak: nNum(raw?.electrical?.W_peak),         // J (absolute peak energy)
      W_cont: nNum(raw?.electrical?.W_cont),         // J (absolute continuous energy)

      // Normalized energy values [Wh]
      W_peak_Wh: jToWh(raw?.electrical?.W_peak),     // Wh (peak energy)
      W_cont_Wh: jToWh(raw?.electrical?.W_cont),     // Wh (continuous energy)

      // Normalized capacity values [Ah]
      C_cont_Ah: cToAh(raw?.electrical?.C_cont),     // Ah (continuous capacity)
      C_peak_Ah: cToAh(raw?.electrical?.C_peak),     // Ah (peak capacity)

      // Peak power and current (for radar chart)
      P_peak: nNum(raw?.electrical?.P_peak),         // W (absolute)
      P_peak_grav: nNum(raw?.electrical?.P_peak_grav), // W/kg (SI unit)
      P_peak_vol: nNum(raw?.electrical?.P_peak_vol),   // W/m³ (SI unit)
      I_peak: nNum(raw?.electrical?.I_peak),         // A (absolute)
      I_peak_grav: nNum(raw?.electrical?.I_peak_grav), // A/kg (SI unit)
      I_peak_vol: nNum(raw?.electrical?.I_peak_vol),   // A/m³ (SI unit)

      I_c10_A: nNum(raw?.electrical?.I_C10),
      P_c10_W: nNum(raw?.electrical?.P_C10),
      I_cont_A: nNum(raw?.electrical?.I_cont),
      I_peak_A: nNum(raw?.electrical?.I_peak),
      I_grav_A_per_kg: nNum(raw?.electrical?.I_peak_grav), // Peak current gravimetric
      I_vol_A_per_L: aPerM3ToAPerL(raw?.electrical?.I_peak_vol), // Peak current volumetric

      P_cont_W: nNum(raw?.electrical?.P_cont),
      P_peak_W: nNum(raw?.electrical?.P_peak),
      P_grav_W_per_kg: nNum(raw?.electrical?.P_peak_grav), // Peak power gravimetric
      P_vol_W_per_L: wPerM3ToWPerL(raw?.electrical?.P_peak_vol), // Peak power volumetric

      // Continuous power densities (calculated from P_cont and mass/volume)
      P_cont_grav: P_cont_grav, // W/kg
      P_cont_vol: P_cont_vol,   // W/m³

      // Continuous current densities (calculated from I_cont and mass/volume)
      I_cont_grav: I_cont_grav, // A/kg
      I_cont_vol: I_cont_vol,   // A/m³

      // Efficiency (%), Resistances (mΩ)
      eta_dc_pct: eta_dc_pct != null ? eta_dc_pct * 100 : null,
      eta_ch_pct: eta_ch_pct != null ? eta_ch_pct * 100 : null,
      efficiency_pct,
      R_dc_mOhm: ohmToMilliOhm(raw?.electrical?.R_dc_25),
      R_ch_mOhm: ohmToMilliOhm(raw?.electrical?.R_ch_25),
      R_ac_mOhm: ohmToMilliOhm(raw?.electrical?.R_ac_25),
      R_EIS_mOhm: ohmToMilliOhm(raw?.electrical?.R_EIS_25),

      // Standard conditions (25°C)
      P_dc_25_W: nNum(raw?.electrical?.P_dc_25),
      P_ch_25_W: nNum(raw?.electrical?.P_ch_25),
      W_dc_25_Wh: jToWh(raw?.electrical?.W_dc_25), // DC Energy at 25°C [Wh]
      W_ch_25_Wh: jToWh(raw?.electrical?.W_ch_25), // Charge Energy at 25°C [Wh]
      I_dc_25_A: nNum(raw?.electrical?.I_dc_25),
      I_ch_25_A: nNum(raw?.electrical?.I_ch_25),
      t_max_dc_25: nNum(raw?.electrical?.t_max_dc_25), // Max discharge time at 25°C [s]
      t_max_ch_25: nNum(raw?.electrical?.t_max_ch_25), // Max charge time at 25°C [s]
      // Standard Capacity from API: C_dc_25 [Ah] (converted from Coulomb if provided)
      C_dc_25_Ah: cToAh(raw?.electrical?.C_dc_25),

      // Standard conditions power/current densities (calculated)
      P_dc_25_grav: P_dc_25_grav, // W/kg
      P_dc_25_vol: P_dc_25_vol,   // W/m³
      I_dc_25_grav: I_dc_25_grav, // A/kg
      I_dc_25_vol: I_dc_25_vol,   // A/m³

      // 4 Experimental Conditions - Discharge Efficiency (fraction → %)
      eta_dc_C10: nNum(raw?.electrical?.eta_dc_C10) != null ? nNum(raw.electrical.eta_dc_C10) * 100 : null,
      eta_dc_cont: nNum(raw?.electrical?.eta_dc_cont) != null ? nNum(raw.electrical.eta_dc_cont) * 100 : null,
      eta_dc_peak: nNum(raw?.electrical?.eta_dc_peak) != null ? nNum(raw.electrical.eta_dc_peak) * 100 : null,

      // 4 Experimental Conditions - Charge Efficiency (fraction → %)
      eta_ch_C10: nNum(raw?.electrical?.eta_ch_C10) != null ? nNum(raw.electrical.eta_ch_C10) * 100 : null,
      eta_ch_cont: nNum(raw?.electrical?.eta_ch_cont) != null ? nNum(raw.electrical.eta_ch_cont) * 100 : null,
      eta_ch_peak: nNum(raw?.electrical?.eta_ch_peak) != null ? nNum(raw.electrical.eta_ch_peak) * 100 : null,

      // 4 Experimental Conditions - Discharge Power [W]
      P_dc_C10: nNum(raw?.electrical?.P_dc_C10),
      P_dc_cont: nNum(raw?.electrical?.P_dc_cont),
      P_dc_peak: nNum(raw?.electrical?.P_dc_peak),

      // 4 Experimental Conditions - Charge Power [W]
      P_ch_C10: nNum(raw?.electrical?.P_ch_C10),
      P_ch_cont: nNum(raw?.electrical?.P_ch_cont),
      P_ch_peak: nNum(raw?.electrical?.P_ch_peak),

      // 4 Experimental Conditions - Discharge Current [A]
      I_dc_C10: nNum(raw?.electrical?.I_dc_C10),
      I_dc_cont: nNum(raw?.electrical?.I_dc_cont),
      I_dc_peak: nNum(raw?.electrical?.I_dc_peak),

      // 4 Experimental Conditions - Charge Current [A]
      I_ch_C10: nNum(raw?.electrical?.I_ch_C10),
      I_ch_cont: nNum(raw?.electrical?.I_ch_cont),
      I_ch_peak: nNum(raw?.electrical?.I_ch_peak),

      // 4 Experimental Conditions - Discharge Time Duration [s]
      t_max_dc_C10: nNum(raw?.electrical?.t_max_dc_C10),
      t_max_dc_cont: nNum(raw?.electrical?.t_max_dc_cont),
      t_max_dc_peak: nNum(raw?.electrical?.t_max_dc_peak),

      // 4 Experimental Conditions - Charge Time Duration [s]
      t_max_ch_C10: nNum(raw?.electrical?.t_max_ch_C10),
      t_max_ch_cont: nNum(raw?.electrical?.t_max_ch_cont),
      t_max_ch_peak: nNum(raw?.electrical?.t_max_ch_peak),

      // Additional Current Metrics
      I_C2: nNum(raw?.electrical?.I_C2), // 2C discharge current [A]

      // Discharge Curve Arrays (DCC - Discharge Constant Current)
      T_amb_DCC: Array.isArray(raw?.electrical?.T_amb_DCC) ? raw.electrical.T_amb_DCC.map(kToC) : [],
      I_DCC: Array.isArray(raw?.electrical?.I_DCC) ? raw.electrical.I_DCC.map(nNum) : [],
      P_DCC: Array.isArray(raw?.electrical?.P_DCC) ? raw.electrical.P_DCC.map(nNum) : [],
      W_DCC: Array.isArray(raw?.electrical?.W_DCC) ? raw.electrical.W_DCC.map(jToWh) : [],
      C_DCC: Array.isArray(raw?.electrical?.C_DCC) ? raw.electrical.C_DCC.map(cToAh) : [],
      t_max_DCC: Array.isArray(raw?.electrical?.t_max_DCC) ? raw.electrical.t_max_DCC.map(nNum) : [],
      eta_DCC: Array.isArray(raw?.electrical?.eta_DCC) ? raw.electrical.eta_DCC.map(nNum) : [],

      // Charge Curve Arrays (CHC - Charge Constant Current)
      T_amb_CHC: Array.isArray(raw?.electrical?.T_amb_CHC) ? raw.electrical.T_amb_CHC.map(kToC) : [],
      I_CHC: Array.isArray(raw?.electrical?.I_CHC) ? raw.electrical.I_CHC.map(nNum) : [],
      P_CHC: Array.isArray(raw?.electrical?.P_CHC) ? raw.electrical.P_CHC.map(nNum) : [],
      W_CHC: Array.isArray(raw?.electrical?.W_CHC) ? raw.electrical.W_CHC.map(jToWh) : [],
      C_CHC: Array.isArray(raw?.electrical?.C_CHC) ? raw.electrical.C_CHC.map(cToAh) : [],
      t_max_CHC: Array.isArray(raw?.electrical?.t_max_CHC) ? raw.electrical.t_max_CHC.map(nNum) : [],
      eta_CHC: Array.isArray(raw?.electrical?.eta_CHC) ? raw.electrical.eta_CHC.map(nNum) : [],

      // DCIR - Discharge (DCP)
      T_amb_DCP: Array.isArray(raw?.electrical?.T_amb_DCP) ? raw.electrical.T_amb_DCP.map(kToC) : [],
      R_10_DCP: Array.isArray(raw?.electrical?.R_10_DCP) ? raw.electrical.R_10_DCP.map(ohmToMilliOhm) : [],
      SOC_DCP: Array.isArray(raw?.electrical?.SOC_DCP) ? raw.electrical.SOC_DCP.map(nNum) : [],
      C_rate_DCP: nNum(raw?.electrical?.C_rate_DCP), // C-rate for discharge pulse test

      // DCIR - Charge (CHP)
      T_amb_CHP: Array.isArray(raw?.electrical?.T_amb_CHP) ? raw.electrical.T_amb_CHP.map(kToC) : [],
      R_10_CHP: Array.isArray(raw?.electrical?.R_10_CHP) ? raw.electrical.R_10_CHP.map(ohmToMilliOhm) : [],
      SOC_CHP: Array.isArray(raw?.electrical?.SOC_CHP) ? raw.electrical.SOC_CHP.map(nNum) : [],
      C_rate_CHP: nNum(raw?.electrical?.C_rate_CHP), // C-rate for charge pulse test

      // EIS (Electrochemical Impedance Spectroscopy) at 25°C
      EIS_25_f: Array.isArray(raw?.electrical?.EIS_25_f) ? raw.electrical.EIS_25_f.map(nNum) : [], // Frequency [Hz]
      EIS_25_Z_real: Array.isArray(raw?.electrical?.EIS_25_Z_real) ? raw.electrical.EIS_25_Z_real.map(ohmToMilliOhm) : [], // Real impedance [mΩ]
      EIS_25_Z_imag: Array.isArray(raw?.electrical?.EIS_25_Z_imag) ? raw.electrical.EIS_25_Z_imag.map(ohmToMilliOhm) : [], // Imaginary impedance [mΩ]

      // Polarization resistance
      R_pol_25: ohmToMilliOhm(raw?.electrical?.R_pol_25), // Polarization resistance [mΩ]

      // C/10 Discharge Voltage Curve (full resolution)
      DCC_C10_V: Array.isArray(raw?.electrical?.DCC_C10_V) ? raw.electrical.DCC_C10_V.map(nNum) : [], // Voltage [V]
      DCC_C10_Q: Array.isArray(raw?.electrical?.DCC_C10_Q) ? raw.electrical.DCC_C10_Q.map(cToAh) : [], // Charge [Ah]

      // Discharge Rate Characteristics (Ivar - varying current)
      DCC_Ivar_t: raw?.electrical?.DCC_Ivar_t || [],
      DCC_Ivar_I: raw?.electrical?.DCC_Ivar_I || [],
      DCC_Ivar_V: raw?.electrical?.DCC_Ivar_V || [],
      DCC_Ivar_Q: raw?.electrical?.DCC_Ivar_Q ? raw.electrical.DCC_Ivar_Q.map(cToAh) : [],
      DCC_Ivar_T_surf: raw?.electrical?.DCC_Ivar_T_surf ? raw.electrical.DCC_Ivar_T_surf.map(kToC) : [],
      DCC_Ivar_C_rate: raw?.electrical?.DCC_Ivar_C_rate || [],
      DCC_Ivar_T_amb: raw?.electrical?.DCC_Ivar_T_amb ? raw.electrical.DCC_Ivar_T_amb.map(kToC) : []

      // Note: DCC_Tvar_* arrays removed from API (temperature variation curves)
    },

    mechanical: {
      // WICHTIG: Beide Einheiten für verschiedene Zwecke!
      m_cell: nNum(raw?.mechanical?.m_cell),  // kg (SI) - für scoreCalculator!
      mass_g: nNum(raw?.mechanical?.m_cell) != null ? raw.mechanical.m_cell * 1000 : null, // g - für UI
      vol_cell_cm3: nNum(raw?.mechanical?.vol_cell) != null ? raw.mechanical.vol_cell * 1e6 : null,
      vol_install_cm3: nNum(raw?.mechanical?.vol_installation) != null ? raw.mechanical.vol_installation * 1e6 : null,
      area_cm2: nNum(raw?.mechanical?.A_surf) != null ? raw.mechanical.A_surf * 1e4 : null,
      format: raw?.mechanical?.format || '',
      format_group: raw?.mechanical?.format_group || '',
      shape: raw?.mechanical?.shape || '', // cylindrical, prismatic, pouch
      dimensions_text: raw?.mechanical?.dimensions || '',
      pole_dimensions_text: raw?.mechanical?.pole_dimensions || '', // renamed from tab_dimensions
      is_braced: raw?.mechanical?.is_braced ?? false, // Whether cell is braced/reinforced

      // Cell dimensions (m → mm)
      r_cell_mm: nNum(raw?.mechanical?.r_cell) != null ? raw.mechanical.r_cell * 1000 : null,
      x_cell_mm: nNum(raw?.mechanical?.x_cell) != null ? raw.mechanical.x_cell * 1000 : null,
      y_cell_mm: nNum(raw?.mechanical?.y_cell) != null ? raw.mechanical.y_cell * 1000 : null,
      z_cell_mm: nNum(raw?.mechanical?.z_cell) != null ? raw.mechanical.z_cell * 1000 : null,

      // Pole/Tab dimensions (m → mm) - Arrays with [pole1, pole2] values
      r_pole_mm: Array.isArray(raw?.mechanical?.r_pole)
        ? raw.mechanical.r_pole.map(v => nNum(v) != null ? v * 1000 : null)
        : (nNum(raw?.mechanical?.r_pole) != null ? [raw.mechanical.r_pole * 1000, raw.mechanical.r_pole * 1000] : [null, null]),
      x_pole_mm: Array.isArray(raw?.mechanical?.x_pole)
        ? raw.mechanical.x_pole.map(v => nNum(v) != null ? v * 1000 : null)
        : (nNum(raw?.mechanical?.x_pole) != null ? [raw.mechanical.x_pole * 1000, raw.mechanical.x_pole * 1000] : [null, null]),
      y_pole_mm: Array.isArray(raw?.mechanical?.y_pole)
        ? raw.mechanical.y_pole.map(v => nNum(v) != null ? v * 1000 : null)
        : (nNum(raw?.mechanical?.y_pole) != null ? [raw.mechanical.y_pole * 1000, raw.mechanical.y_pole * 1000] : [null, null]),
      z_pole_mm: Array.isArray(raw?.mechanical?.z_pole)
        ? raw.mechanical.z_pole.map(v => nNum(v) != null ? v * 1000 : null)
        : (nNum(raw?.mechanical?.z_pole) != null ? [raw.mechanical.z_pole * 1000, raw.mechanical.z_pole * 1000] : [null, null]),

      // Pouch core dimensions (m → mm)
      x_pouchcore_mm: nNum(raw?.mechanical?.x_pouchcore) != null ? raw.mechanical.x_pouchcore * 1000 : null,
      y_pouchcore_mm: nNum(raw?.mechanical?.y_pouchcore) != null ? raw.mechanical.y_pouchcore * 1000 : null,

      // Pole location/orientation
      pole_location: raw?.mechanical?.pole_location || '' // 'same-side (x)', 'opposite-side (x)', 'same-side (y)', 'opposite-side (y)', 'top-bottom', 'top-top'
    },

    // Safety features (boolean flags)
    safety: {
      ISP: raw?.safety?.ISP ?? false,    // Internal Short Protection
      PTC: raw?.safety?.PTC ?? false,    // Positive Temperature Coefficient (resettable fuse)
      CID: raw?.safety?.CID ?? false,    // Current Interrupt Device
      BURST: raw?.safety?.BURST ?? false, // Burst disk
      VENT: raw?.safety?.VENT ?? false   // Vent holes
    },

    thermal: (() => {
      console.log('[dataService] === COMPUTING THERMAL VALUES (not from cache) ===');
      // Helper to find thermal values for 4 experimental conditions by matching P/I/T_amb from electrical arrays
      const findThermalValues = () => {
        const P_DCC = Array.isArray(raw?.electrical?.P_DCC) ? raw.electrical.P_DCC : [];
        const I_DCC = Array.isArray(raw?.electrical?.I_DCC) ? raw.electrical.I_DCC : [];
        const T_amb_DCC = Array.isArray(raw?.electrical?.T_amb_DCC) ? raw.electrical.T_amb_DCC : [];
        const T_max_DCC = Array.isArray(raw?.thermal?.T_max_DCC) ? raw.thermal.T_max_DCC : [];
        const P_loss_DCC = Array.isArray(raw?.thermal?.P_loss_DCC) ? raw.thermal.P_loss_DCC : [];
        const W_loss_DCC = Array.isArray(raw?.thermal?.W_loss_DCC) ? raw.thermal.W_loss_DCC : [];
        const eta_DCC = Array.isArray(raw?.electrical?.eta_DCC) ? raw.electrical.eta_DCC : [];

        // Reference values from electrical section (SI units)
        const P_C10 = nNum(raw?.electrical?.P_C10) || 0;
        const P_dc_25 = nNum(raw?.electrical?.P_dc_25) || 0;
        const P_cont = nNum(raw?.electrical?.P_cont) || 0;
        const P_peak = nNum(raw?.electrical?.P_peak) || 0;

        // Find closest match in DCC arrays at 25°C (298.15K)
        const findClosestAt25C = (targetP) => {
          let closestIdx = -1;
          let minDist = Infinity;

          for (let i = 0; i < P_DCC.length; i++) {
            // Only consider points at ~25°C (298.15K ± 1K)
            const T_amb = T_amb_DCC[i];
            if (Math.abs(T_amb - 298.15) > 1) continue;

            const dist = Math.abs(P_DCC[i] - targetP);
            if (dist < minDist) {
              minDist = dist;
              closestIdx = i;
            }
          }

          return closestIdx;
        };

        const idx_c10 = findClosestAt25C(P_C10);
        const idx_dc_25 = findClosestAt25C(P_dc_25);
        const idx_cont = findClosestAt25C(P_cont);
        const idx_peak = findClosestAt25C(P_peak);

        // DEBUG: Log thermal calculations
        console.log('[dataService] findThermalValues indices:', { idx_c10, idx_dc_25, idx_cont, idx_peak });
        console.log('[dataService] P references:', { P_C10, P_dc_25, P_cont, P_peak });
        console.log('[dataService] Array lengths:', { P_DCC: P_DCC.length, P_loss_DCC: P_loss_DCC.length, T_amb_DCC: T_amb_DCC.length });

        return {
          // Power loss [W] for 4 conditions
          P_loss_c10: idx_c10 >= 0 ? nNum(P_loss_DCC[idx_c10]) : 0,
          P_loss_dc_25: nNum(raw?.thermal?.P_loss_dc_25) || 0, // Use scalar if available
          P_loss_cont: idx_cont >= 0 ? nNum(P_loss_DCC[idx_cont]) : 0,
          P_loss_peak: idx_peak >= 0 ? nNum(P_loss_DCC[idx_peak]) : 0,

          // Energy loss [Wh] for 4 conditions
          W_loss_c10: idx_c10 >= 0 ? jToWh(W_loss_DCC[idx_c10]) : 0,
          W_loss_dc_25: jToWh(raw?.thermal?.W_loss_dc_25) || 0, // Use scalar if available
          W_loss_cont: idx_cont >= 0 ? jToWh(W_loss_DCC[idx_cont]) : 0,
          W_loss_peak: idx_peak >= 0 ? jToWh(W_loss_DCC[idx_peak]) : 0,

          // Max temperature [°C] for 4 conditions (convert from K)
          T_max_c10: idx_c10 >= 0 ? kToC(T_max_DCC[idx_c10]) : 0,
          T_max_dc_25: kToC(raw?.thermal?.T_max_dc_25) || 0, // Use scalar if available
          T_max_cont: idx_cont >= 0 ? kToC(T_max_DCC[idx_cont]) : 0,
          T_max_peak: idx_peak >= 0 ? kToC(T_max_DCC[idx_peak]) : 0,

          // Efficiency [fraction] for 4 conditions (convert to %)
          eta_c10: idx_c10 >= 0 ? nNum(eta_DCC[idx_c10]) * 100 : 0,
          eta_dc_25: nNum(raw?.electrical?.eta_dc_25) * 100 || 0, // Use scalar from electrical
          eta_cont: idx_cont >= 0 ? nNum(eta_DCC[idx_cont]) * 100 : 0,
          eta_peak: idx_peak >= 0 ? nNum(eta_DCC[idx_peak]) * 100 : 0,
        };
      };

      const thermalValues = findThermalValues();

      return {
        // 4 experimental condition values (scalar)
        P_loss_c10: thermalValues.P_loss_c10,
        P_loss_dc_25: thermalValues.P_loss_dc_25,
        P_loss_cont: thermalValues.P_loss_cont,
        P_loss_peak: thermalValues.P_loss_peak,

        W_loss_c10: thermalValues.W_loss_c10,
        W_loss_dc_25: thermalValues.W_loss_dc_25,
        W_loss_cont: thermalValues.W_loss_cont,
        W_loss_peak: thermalValues.W_loss_peak,

        T_max_c10: thermalValues.T_max_c10,
        T_max_dc_25: thermalValues.T_max_dc_25,
        T_max_cont: thermalValues.T_max_cont,
        T_max_peak: thermalValues.T_max_peak,

        eta_c10: thermalValues.eta_c10,
        eta_dc_25: thermalValues.eta_dc_25,
        eta_cont: thermalValues.eta_cont,
        eta_peak: thermalValues.eta_peak,

        // Arrays for 2D/3D plots
        E_loss_chc_Wh: Array.isArray(raw?.thermal?.W_loss_CHC) ? raw.thermal.W_loss_CHC.map(jToWh) : null,
        E_loss_dcc_Wh: Array.isArray(raw?.thermal?.W_loss_DCC) ? raw.thermal.W_loss_DCC.map(jToWh) : null,
        P_loss_chc_W: Array.isArray(raw?.thermal?.P_loss_CHC) ? raw.thermal.P_loss_CHC.map(nNum) : null,
        P_loss_dcc_W: Array.isArray(raw?.thermal?.P_loss_DCC) ? raw.thermal.P_loss_DCC.map(nNum) : null,
        T_max_chc_C: Array.isArray(raw?.thermal?.T_max_CHC) ? raw.thermal.T_max_CHC.map(kToC) : null,
        T_max_dcc_C: Array.isArray(raw?.thermal?.T_max_DCC) ? raw.thermal.T_max_DCC.map(kToC) : null,
        T_max_DCC: Array.isArray(raw?.thermal?.T_max_DCC) ? raw.thermal.T_max_DCC.map(kToC) : [],
        W_loss_DCC: Array.isArray(raw?.thermal?.W_loss_DCC) ? raw.thermal.W_loss_DCC.map(jToWh) : [],
        P_loss_DCC: Array.isArray(raw?.thermal?.P_loss_DCC) ? raw.thermal.P_loss_DCC.map(nNum) : [],

        // Delta T (deprecated - use T_max instead)
        delta_T_dc_25: nNum(raw?.thermal?.delta_T_dc_25),
        delta_T_ch_25: nNum(raw?.thermal?.delta_T_ch_25),
        deltaT_ch_25_K: nNum(raw?.thermal?.delta_T_ch_25),
        deltaT_dc_25_K: nNum(raw?.thermal?.delta_T_dc_25),

        // Charging values (not used in 4-condition system)
        P_loss_ch_25: nNum(raw?.thermal?.P_loss_ch_25),
        W_loss_ch_25: jToWh(raw?.thermal?.W_loss_ch_25),

        // 4 Experimental Conditions - Discharge Energy Loss [J → Wh]
        W_loss_dc_C10: jToWh(raw?.thermal?.W_loss_dc_C10),
        W_loss_dc_cont: jToWh(raw?.thermal?.W_loss_dc_cont),
        W_loss_dc_peak: jToWh(raw?.thermal?.W_loss_dc_peak),

        // 4 Experimental Conditions - Charge Energy Loss [J → Wh]
        W_loss_ch_C10: jToWh(raw?.thermal?.W_loss_ch_C10),
        W_loss_ch_cont: jToWh(raw?.thermal?.W_loss_ch_cont),
        W_loss_ch_peak: jToWh(raw?.thermal?.W_loss_ch_peak),

        // 4 Experimental Conditions - Discharge Power Loss [W]
        P_loss_dc_C10: nNum(raw?.thermal?.P_loss_dc_C10),
        P_loss_dc_cont: nNum(raw?.thermal?.P_loss_dc_cont),
        P_loss_dc_peak: nNum(raw?.thermal?.P_loss_dc_peak),

        // 4 Experimental Conditions - Charge Power Loss [W]
        P_loss_ch_C10: nNum(raw?.thermal?.P_loss_ch_C10),
        P_loss_ch_cont: nNum(raw?.thermal?.P_loss_ch_cont),
        P_loss_ch_peak: nNum(raw?.thermal?.P_loss_ch_peak),

        // 4 Experimental Conditions - Discharge Max Temperature [K → °C]
        T_max_dc_C10: kToC(raw?.thermal?.T_max_dc_C10),
        T_max_dc_cont: kToC(raw?.thermal?.T_max_dc_cont),
        T_max_dc_peak: kToC(raw?.thermal?.T_max_dc_peak),

        // 4 Experimental Conditions - Charge Max Temperature [K → °C]
        T_max_ch_C10: kToC(raw?.thermal?.T_max_ch_C10),
        T_max_ch_cont: kToC(raw?.thermal?.T_max_ch_cont),
        T_max_ch_peak: kToC(raw?.thermal?.T_max_ch_peak),

        // 4 Experimental Conditions - Discharge Temperature Rise [K]
        delta_T_dc_C10: nNum(raw?.thermal?.delta_T_dc_C10),
        delta_T_dc_cont: nNum(raw?.thermal?.delta_T_dc_cont),
        delta_T_dc_peak: nNum(raw?.thermal?.delta_T_dc_peak),

        // 4 Experimental Conditions - Charge Temperature Rise [K]
        delta_T_ch_C10: nNum(raw?.thermal?.delta_T_ch_C10),
        delta_T_ch_cont: nNum(raw?.thermal?.delta_T_ch_cont),
        delta_T_ch_peak: nNum(raw?.thermal?.delta_T_ch_peak),

        // Standard Gravimetric Power Limit [W/kg]
        P_grav_standard: nNum(raw?.thermal?.P_grav_standard)
      };
    })(),

    // DCC performance arrays (for 2D/3D plots) - absolute values only
    electrical_W_DCC: Array.isArray(raw?.electrical?.W_DCC) ? raw.electrical.W_DCC.map(jToWh) : [],
    electrical_C_DCC: Array.isArray(raw?.electrical?.C_DCC) ? raw.electrical.C_DCC.map(cToAh) : [],
    electrical_P_DCC: Array.isArray(raw?.electrical?.P_DCC) ? raw.electrical.P_DCC.map(nNum) : [],
    electrical_I_DCC: Array.isArray(raw?.electrical?.I_DCC) ? raw.electrical.I_DCC.map(nNum) : [],
    electrical_T_amb_DCC: Array.isArray(raw?.electrical?.T_amb_DCC) ? raw.electrical.T_amb_DCC.map(kToC) : [],
    electrical_t_max_DCC: Array.isArray(raw?.electrical?.t_max_DCC) ? raw.electrical.t_max_DCC.map(nNum) : [],

    // C/10 Voltage curve
    electrical_DCC_C10_V: Array.isArray(raw?.electrical?.DCC_C10_V) ? raw.electrical.DCC_C10_V.map(nNum) : [],
    electrical_DCC_C10_Q: Array.isArray(raw?.electrical?.DCC_C10_Q) ? raw.electrical.DCC_C10_Q.map(cToAh) : [],

    // Batch variation (for KPI tiles and quality section)
    batch: {
      // Capacity spread
      CAP_spread: Array.isArray(raw?.batch?.CAP_spread) ? raw.batch.CAP_spread.map(cToAh) : [], // Individual cell capacities [Ah]
      CAP_spread_mean: cToAh(raw?.batch?.CAP_spread_mean), // Mean capacity [Ah]
      CAP_spread_std_abs: cToAh(raw?.batch?.CAP_spread_std_abs), // Absolute standard deviation [Ah]
      CAP_spread_std_rel: nNum(raw?.batch?.CAP_spread_std_rel), // Relative standard deviation (fraction)
      CAP_spread_min: cToAh(raw?.batch?.CAP_spread_min), // Min capacity [Ah]
      CAP_spread_max: cToAh(raw?.batch?.CAP_spread_max), // Max capacity [Ah]
      // DEMO teaser band ("low"|"medium"|"high"|null) when the exact spread is
      // withheld — pass-through from the detail policy (null for PRO/admin).
      CAP_spread_band: raw?.batch?.CAP_spread_band ?? null,

      // EIS/Resistance spread
      EIS_spread: Array.isArray(raw?.batch?.EIS_spread) ? raw.batch.EIS_spread.map(ohmToMilliOhm) : [], // Individual cell resistances [mΩ]
      EIS_spread_mean: ohmToMilliOhm(raw?.batch?.EIS_spread_mean), // Mean resistance [mΩ]
      EIS_spread_std_abs: ohmToMilliOhm(raw?.batch?.EIS_spread_std_abs), // Absolute std dev [mΩ]
      EIS_spread_std_rel: nNum(raw?.batch?.EIS_spread_std_rel) // Relative std dev (fraction)
    },

    // Aging (for KPI tiles and quality section)
    aging: {
      CAP_loss_rel: nNum(raw?.aging?.CAP_loss_rel), // Initial aging - relative capacity loss (fraction)
      CAP_loss: cToAh(raw?.aging?.CAP_loss), // Absolute capacity loss [Ah]
      // DEMO teaser band ("low"|"medium"|"high"|null) when the exact loss is
      // withheld — pass-through from the detail policy (null for PRO/admin).
      CAP_loss_band: raw?.aging?.CAP_loss_band ?? null,
      EIS_increase_rel: nNum(raw?.aging?.EIS_increase_rel), // Relative resistance increase (fraction)
      EIS_increase: ohmToMilliOhm(raw?.aging?.EIS_increase), // Absolute resistance increase [mΩ]

      // Aging curves (arrays)
      CAP_T: Array.isArray(raw?.aging?.CAP_T) ? raw.aging.CAP_T.map(kToC) : [], // Temperature [°C]
      CAP_Qtp_0: Array.isArray(raw?.aging?.CAP_Qtp_0) ? raw.aging.CAP_Qtp_0.map(cToAh) : [], // Capacity at 0 cycles [Ah]
      CAP_Qtp_standard: Array.isArray(raw?.aging?.CAP_Qtp_standard) ? raw.aging.CAP_Qtp_standard.map(cToAh) : [], // Capacity after standard aging [Ah]

      EIS_T: Array.isArray(raw?.aging?.EIS_T) ? raw.aging.EIS_T.map(kToC) : [], // Temperature [°C]
      EIS_Qtp_0: Array.isArray(raw?.aging?.EIS_Qtp_0) ? raw.aging.EIS_Qtp_0.map(ohmToMilliOhm) : [], // Resistance at 0 cycles [mΩ]
      EIS_Qtp_standard: Array.isArray(raw?.aging?.EIS_Qtp_standard) ? raw.aging.EIS_Qtp_standard.map(ohmToMilliOhm) : [], // Resistance after standard aging [mΩ]

      Qtp_standard: nNum(raw?.aging?.Qtp_standard), // Standard throughput [C] (SI unit)

      // Initial Aging Metric
      EFC_initial_aging: nNum(raw?.aging?.EFC_initial_aging) // Equivalent Full Cycles for initial aging [cycles]
    },

    // DEMO metric-tiering: cell-shape keys whose exact value the detail policy
    // withheld (empty for PRO/admin/internal). Detail sections key off this to
    // render a PRO lock token instead of a bare "—". Pass-through + translated
    // via the SAME map the explorer shaping uses, so the gate stays in sync.
    // Reads either the top-level response field or the categories block.
    _gated: gatedFieldsToCellShapeKeys(
      rawResponse?.gated_fields || raw?.gated_fields || []
    )
  };

  console.log('[getCellNormalized] Normalized result:', result);
  return result;
}
/**
 * Build a list URL (explorer) with optional query params
 * cfg supports { url, baseUrl }
 */
export function buildListUrl(cfg = {}, query = {}) {
  const base = (cfg.url || cfg.baseUrl || '').replace(/\/$/, '');
  if (!base) return null;
  const usp = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  });
  return usp.toString() ? `${base}?${usp.toString()}` : base;
}

/**
 * Fetch product list for explorer (e.g., /api/v1/products)
 * Reads window.BATEMO_API.list by default. Supports query: { page, limit, search }
 */
export async function getProductList(overrides = {}) {
  startPhase('PRODUCT_LIST');

  const cfg = overrides.config || (window.BATEMO_API && window.BATEMO_API.list) || {};
  const url = overrides.url || buildListUrl(cfg, overrides.query);
  const token = overrides.token ?? cfg.token ?? null;
  const tokenParam = overrides.tokenParam ?? cfg.tokenParam ?? null;
  const headerName = overrides.headerName ?? cfg.headerName ?? null;
  const headerPrefix = overrides.headerPrefix ?? cfg.headerPrefix ?? null;
  if (!url) throw new Error('getProductList: no URL provided');

  updatePhaseProgress('PRODUCT_LIST', 0.5);

  const result = await apiGet(url, {
    token,
    tokenParam,
    headerName,
    headerPrefix,
    query: overrides.query,
    timeout: overrides.timeout,
    maxAge: CACHE_DURATION.PRODUCT_LIST
  });

  completePhase('PRODUCT_LIST');
  return result;
}

/**
 * Batch-fetch multiple cells in parallel (with concurrency limit)
 * @param {string[]} cellIds - Array of cell IDs to fetch
 * @param {object} overrides - Same as getCellNormalized overrides
 * @param {number} concurrency - Max concurrent requests (default: 5)
 * @returns {Promise<Map>} Map of cellId → normalized cell data
 */
export async function batchGetCells(cellIds, overrides = {}, concurrency = 5) {
  const results = new Map();
  const errors = new Map();

  console.log(`[dataService] Batch fetching ${cellIds.length} cells (concurrency: ${concurrency})`);
  const startTime = Date.now();

  startPhase('MINIMAL_DATA');

  // Process in batches
  for (let i = 0; i < cellIds.length; i += concurrency) {
    const batch = cellIds.slice(i, i + concurrency);
    const batchResults = await Promise.allSettled(
      batch.map(id => getCellNormalized(id, overrides))
    );

    batchResults.forEach((result, idx) => {
      const cellId = batch[idx];
      if (result.status === 'fulfilled') {
        results.set(cellId, result.value);
      } else {
        errors.set(cellId, result.reason);
        console.warn(`[dataService] Failed to fetch cell ${cellId}:`, result.reason);
      }
    });

    // Emit progress after each batch
    const progress = (i + batch.length) / cellIds.length;
    updatePhaseProgress('MINIMAL_DATA', progress);
  }

  const duration = Date.now() - startTime;
  console.log(`[dataService] Batch complete: ${results.size}/${cellIds.length} successful in ${duration}ms`);

  if (errors.size > 0) {
    console.warn(`[dataService] ${errors.size} cells failed to load`);
  }

  completePhase('MINIMAL_DATA');
  return results;
}

/**
 * Get a signed URL for a single cell picture.
 * This returns a URL that can be used directly in <img src=""> without auth headers.
 * More performant than getCellPicture() which downloads and converts to base64.
 *
 * @param {string} cellId - Cell product ID
 * @param {string} imageName - Image filename (e.g., 'report_drawing_exterior.svg')
 * @param {object} options - Options
 * @param {number} options.expiresIn - URL validity in seconds (default: 3600)
 * @returns {Promise<string|null>} Signed URL or null on error
 */
export async function getSignedPictureUrl(cellId, imageName, options = {}) {
  const cfg = window.BATEMO_API?.cell || {};
  // Use same baseUrl logic as getCellPicture
  const baseUrl = options.baseUrl || window.BATEMO_API?.baseUrl || '';
  const apiBase = baseUrl.replace(/\/?$/, '');

  const { expiresIn = 3600 } = options;

  // Cache key for signed URLs (shorter TTL since URLs expire)
  const cacheKey = `signed-url:${cellId}:${imageName}`;

  // Check memory cache (signed URLs cached for shorter time)
  const cached = _cache.get(cacheKey);
  if (cached) {
    const age = Date.now() - cached.ts;
    const maxAge = Math.min(expiresIn * 1000 * 0.8, 30 * 60 * 1000); // 80% of expiry or 30min max
    if (age < maxAge) {
      console.log(`[dataService] 🔗 Signed URL cache HIT: ${imageName}`);
      return cached.data;
    }
  }

  try {
    const jwtToken = getAccessToken();
    const headers = {};

    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }

    // Request signed URLs for this product
    const url = `${apiBase}/products/${cellId}/pictures/signed-urls?expires_in=${expiresIn}`;
    const res = await fetch(url, { headers });

    if (!res.ok) {
      console.warn(`[dataService] Failed to get signed URLs: ${res.status}`);
      return null;
    }

    const data = await res.json();

    // Find the requested picture
    const picture = data.pictures?.find(p => p.name === imageName);
    if (!picture?.url) {
      console.warn(`[dataService] Picture ${imageName} not found in signed URLs response`);
      return null;
    }

    // Cache the URL
    _cache.set(cacheKey, { ts: Date.now(), data: picture.url });

    console.log(`[dataService] 🔗 Signed URL generated: ${imageName} (expires in ${expiresIn}s)`);
    return picture.url;

  } catch (err) {
    console.warn(`[dataService] Failed to get signed URL for ${imageName}:`, err);
    return null;
  }
}

/**
 * Get SVG content with caching (optimized for technical drawings)
 * Caches the actual SVG text content for instant subsequent loads
 *
 * @param {string} cellId - Cell product ID
 * @param {string} imageName - SVG filename (e.g., 'report_drawing_exterior.svg')
 * @returns {Promise<string|null>} SVG text content or null on error
 */
export async function getSvgContent(cellId, imageName) {
  const cacheKey = `svg-content:${cellId}:${imageName}`;

  // Check memory cache first (instant)
  const memCached = _cache.get(cacheKey);
  if (memCached) {
    const age = Date.now() - memCached.ts;
    if (age < CACHE_DURATION.CELL_DATA) {
      console.log(`[dataService] 🎨 SVG memory cache HIT: ${imageName}`);
      return memCached.data;
    }
  }

  // Check IndexedDB cache
  try {
    const idbCached = await getIndexedDBCache(cacheKey);
    if (idbCached?.data) {
      const age = Date.now() - (idbCached.ts || 0);
      if (age < CACHE_DURATION.CELL_DATA) {
        console.log(`[dataService] 🎨 SVG IndexedDB cache HIT: ${imageName}`);
        // Populate memory cache
        _cache.set(cacheKey, { ts: idbCached.ts, data: idbCached.data });
        return idbCached.data;
      }
    }
  } catch (e) {
    console.warn('[dataService] IndexedDB read error:', e);
  }

  // Deduplicate concurrent requests
  if (_inflight.has(cacheKey)) {
    console.log(`[dataService] 🎨 SVG request deduplicated: ${imageName}`);
    return _inflight.get(cacheKey);
  }

  const fetchPromise = (async () => {
    try {
      // Get signed URL
      const signedUrl = await getSignedPictureUrl(cellId, imageName);
      if (!signedUrl) return null;

      // Fetch SVG content
      const response = await fetch(signedUrl, {
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.status}`);
      }

      const svgText = await response.text();

      if (svgText) {
        // Cache in memory
        _cache.set(cacheKey, { ts: Date.now(), data: svgText });

        // Cache in IndexedDB (async, don't await)
        setIndexedDBCache(cacheKey, { ts: Date.now(), data: svgText }).catch(e => {
          console.warn('[dataService] IndexedDB write error:', e);
        });

        console.log(`[dataService] 🎨 SVG fetched and cached: ${imageName} (${(svgText.length / 1024).toFixed(1)}KB)`);
      }

      return svgText;
    } catch (err) {
      console.error(`[dataService] Failed to fetch SVG ${imageName}:`, err);
      return null;
    } finally {
      _inflight.delete(cacheKey);
    }
  })();

  _inflight.set(cacheKey, fetchPromise);
  return fetchPromise;
}

/**
 * Get cell picture with IndexedDB caching
 * @param {string} cellId - Cell product ID
 * @param {string} imageName - Image filename (e.g., 'website_picture_cell1.png')
 * @param {object} overrides - Override config (token, baseUrl)
 * @returns {Promise<string|null>} Object URL for the image blob, or null on error
 */
export async function getCellPicture(cellId, imageName, overrides = {}) {
  const cfg = window.BATEMO_API?.cell || {};
  const baseUrl = overrides.baseUrl || window.BATEMO_API?.baseUrl || '';
  const token = overrides.token || cfg.token || _defaultToken;
  const url = `${baseUrl}/products/${cellId}/pictures/${imageName}`;

  // Cache key includes URL
  const cacheKey = url;

  // Check in-memory cache first
  const cached = _cache.get(cacheKey);
  if (cached) {
    const age = Date.now() - cached.ts;
    const maxAge = CACHE_DURATION.CELL_DATA; // 8 hours for images
    if (age < maxAge) {
      const ageMin = (age / 1000 / 60).toFixed(1);
      console.log(`[dataService] 🖼️ Image cache HIT: ${imageName} (age: ${ageMin} min, cached in IndexedDB)`);
      // Cached data is base64 data URL
      return cached.data;
    } else {
      console.log(`[dataService] ⏰ Image cache EXPIRED: ${imageName} (age: ${(age / 1000 / 60 / 60).toFixed(1)}h)`);
    }
  }

  try {
    // Fetch image with auth headers
    const headers = {};

    // Add JWT token if available
    const jwtToken = getAccessToken();
    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }

    // Override if custom token provided
    if (token && token !== jwtToken) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(url, { headers });
    if (!res.ok) {
      // Handle 401 Unauthorized - try to refresh token
      if (res.status === 401 && !isDevelopment()) {
        console.warn(`[dataService] Image fetch unauthorized, trying token refresh`);
        try {
          const { refreshAccessToken } = await import('../services/authService.js');
          await refreshAccessToken();

          // Retry with new token
          const newJwtToken = getAccessToken();
          if (newJwtToken) {
            const newHeaders = {
              'Authorization': `Bearer ${newJwtToken}`
            };

            const retryRes = await fetch(url, { headers: newHeaders });
            if (retryRes.ok) {
              console.log(`[dataService] ✓ Image retry successful after token refresh`);
              const blob = await retryRes.blob();

              // Convert blob to base64 data URL for caching
              const dataUrl = await readBlobAsDataUrl(blob);
              if (!dataUrl) {
                console.warn(`[dataService] Image blob unreadable after retry: ${imageName}`);
                return null;
              }

              // Store in cache
              _cache.set(cacheKey, { ts: Date.now(), data: dataUrl });
              persistCache();

              const sizeKB = (dataUrl.length / 1024).toFixed(1);
              console.log(`[dataService] 📥 Image LOADED and cached after retry: ${imageName} (${sizeKB} KB)`);
              return dataUrl;
            }
          }
        } catch (refreshError) {
          console.error('[dataService] Token refresh failed for image:', refreshError);
          // Clear invalid tokens and logout
          const { logout } = await import('../services/authService.js');
          logout();
        }
      }

      console.warn(`[dataService] Image not found: ${imageName} (${res.status})`);
      return null;
    }

    const blob = await res.blob();

    // Convert blob to base64 data URL for caching
    const dataUrl = await readBlobAsDataUrl(blob);
    if (!dataUrl) {
      console.warn(`[dataService] Image blob unreadable: ${imageName}`);
      return null;
    }

    // Store in cache
    _cache.set(cacheKey, { ts: Date.now(), data: dataUrl });

    // Persist to IndexedDB (debounced)
    persistCache();

    const sizeKB = (dataUrl.length / 1024).toFixed(1);
    console.log(`[dataService] 📥 Image LOADED and cached: ${imageName} (${sizeKB} KB, will persist to IndexedDB)`);
    return dataUrl;

  } catch (err) {
    console.warn(`[dataService] Failed to load picture ${imageName} for ${cellId}:`, err);
    return null;
  }
}

/**
 * Get signed URLs for multiple cell pictures in one batch request.
 * More efficient than calling getCellPicture() for each cell.
 *
 * @param {string[]} cellIds - Array of cell/product IDs (max 50)
 * @param {object} options - Options
 * @param {string} options.picName - Picture name (default: 'website_picture_cell1.png')
 * @param {number} options.expiresIn - URL validity in seconds (default: 3600)
 * @param {string} options.thumbnailSize - Thumbnail size e.g. '200x200' (optional)
 * @returns {Promise<Map<string, string>>} Map of productId → signed URL
 */
export async function getBatchPictureUrls(cellIds, options = {}) {
  const cfg = window.BATEMO_API?.list || {};
  const baseUrl = cfg.url || cfg.baseUrl || '';
  const url = `${baseUrl.replace(/\/?$/, '')}/pictures/batch`;

  const {
    picName = 'website_picture_cell1.png',
    expiresIn = 3600,
    thumbnailSize = null
  } = options;

  console.log(`[dataService] 📦 Fetching batch picture URLs for ${cellIds.length} cells...`);

  try {
    const jwtToken = getAccessToken();
    const headers = {
      'Content-Type': 'application/json'
    };

    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }

    const body = {
      product_ids: cellIds,
      pic_name: picName,
      expires_in: expiresIn
    };

    if (thumbnailSize) {
      body.thumbnail_size = thumbnailSize;
    }

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      throw new Error(`Batch picture request failed: ${res.status}`);
    }

    assertJsonResponse(res, url);
    const data = await res.json();

    // Convert response to Map for easy lookup
    const urlMap = new Map();
    for (const item of data.pictures || []) {
      if (item.url && !item.error) {
        urlMap.set(item.product_id, item.url);
      }
    }

    console.log(`[dataService] ✓ Batch picture URLs: ${urlMap.size}/${cellIds.length} successful`);
    return urlMap;

  } catch (err) {
    console.warn('[dataService] Batch picture request failed:', err);
    return new Map();
  }
}

/**
 * Preload cell data in the background (fire-and-forget)
 * Useful for preloading cells that the user is likely to open
 * @param {string|string[]} cellIds - Cell ID(s) to preload
 * @param {object} overrides - Same as getCellNormalized overrides
 */
export function preloadCells(cellIds, overrides = {}) {
  const ids = Array.isArray(cellIds) ? cellIds : [cellIds];

  // Fire-and-forget, don't await
  Promise.all(
    ids.map(id => getCellNormalized(id, overrides).catch(err => {
      console.warn(`[dataService] Preload failed for ${id}:`, err);
    }))
  ).then(() => {
    console.log(`[dataService] Preloaded ${ids.length} cells`);
  });
}

/**
 * Get cache statistics for debugging
 */
export function getCacheStats() {
  hydrateCache();

  const now = Date.now();
  const stats = {
    total: _cache.size,
    byAge: { fresh: 0, stale: 0 },
    byType: { products: 0, cells: 0, other: 0 },
    totalSize: 0
  };

  _cache.forEach((value, key) => {
    const age = now - value.ts;
    const maxAge = getMaxAgeForKey(key);

    if (age < maxAge) {
      stats.byAge.fresh++;
    } else {
      stats.byAge.stale++;
    }

    if (key.includes('/products/')) stats.byType.cells++;
    else if (key.includes('/products')) stats.byType.products++;
    else stats.byType.other++;

    try {
      stats.totalSize += JSON.stringify(value.data).length;
    } catch (e) { }
  });

  stats.totalSizeMB = (stats.totalSize / 1024 / 1024).toFixed(2);

  return stats;
}

// Global API for debugging (available in console)
if (typeof window !== 'undefined') {
  window.__BATEMO_API_CACHE__ = {
    stats: getCacheStats,
    clear: clearCache,
    refresh: refreshCache,
    get: (url) => _cache.get(url),
    list: () => Array.from(_cache.keys()),
    info: async () => {
      const stats = getCacheStats();
      const idbStats = await window.__INDEXEDDB_CACHE__.stats();

      console.group('🗄️ Batemo API Cache');
      console.log(`Memory Cache: ${stats.total} entries`);
      console.log(`  Fresh: ${stats.byAge.fresh} | Stale: ${stats.byAge.stale}`);
      console.log(`  Products: ${stats.byType.products} | Cells: ${stats.byType.cells} | Other: ${stats.byType.other}`);
      console.log(`  Size: ${stats.totalSizeMB} MB`);
      console.log(`\nIndexedDB Cache: ${idbStats.count} entries`);
      console.log(`  Size: ${(idbStats.totalSize / 1024 / 1024).toFixed(2)} MB`);
      console.log(`\nCache durations (with backend caching):`);
      console.log(`  - Product List: ${CACHE_DURATION.PRODUCT_LIST / 1000 / 60 / 60 / 24} days`);
      console.log(`  - Cell Data: ${CACHE_DURATION.CELL_DATA / 1000 / 60 / 60}h`);
      console.log(`  - Filter Metadata: ${CACHE_DURATION.FILTER_METADATA / 1000 / 60 / 60 / 24} days`);
      console.log(`  - Default: ${CACHE_DURATION.DEFAULT / 1000 / 60}min`);
      console.log(`\n💡 Use __BATEMO_API_CACHE__.refresh(url) to force update`);
      console.groupEnd();

      return { memory: stats, indexedDB: idbStats };
    },
    // Performance test
    benchmark: async () => {
      console.log('🏎️ Running API cache benchmark...');
      const testUrl = window.BATEMO_API?.list?.url || '';
      if (!testUrl) {
        console.error('No API URL configured');
        return;
      }

      // Clear cache first
      await clearCache();

      // First request (cold cache)
      const t1 = performance.now();
      try {
        await getProductList();
        const coldTime = performance.now() - t1;

        // Second request (hot cache)
        const t2 = performance.now();
        await getProductList();
        const hotTime = performance.now() - t2;

        console.log(`Cold cache: ${coldTime.toFixed(2)}ms`);
        console.log(`Hot cache: ${hotTime.toFixed(2)}ms`);
        console.log(`Speed up: ${(coldTime / hotTime).toFixed(1)}x faster`);
      } catch (e) {
        console.error('Benchmark failed:', e);
      }
    }
  };

  console.log('[dataService] 💡 Tip: Use window.__BATEMO_API_CACHE__ for cache debugging');
  console.log('  - __BATEMO_API_CACHE__.info() - Show cache stats');
  console.log('  - __BATEMO_API_CACHE__.clear() - Clear all cache');
  console.log('  - __BATEMO_API_CACHE__.refresh(url, options) - Force refresh specific URL');
  console.log('  - __BATEMO_API_CACHE__.benchmark() - Test cache performance');
}

/**
 * Normalize common list responses to an array of { id, name, manufacturer }
 */
export function normalizeProductList(json) {
  // 1) { items: [ { id, name, manufacturer }, ... ] }
  if (Array.isArray(json?.items)) return json.items.map(nrmProductShape);
  // 2) direct array
  if (Array.isArray(json)) return json.map(nrmProductShape);
  // 3) { data: [...] }
  if (Array.isArray(json?.data)) return json.data.map(nrmProductShape);
  return [];
}

function nrmProductShape(p) {
  if (!p || typeof p !== 'object') return { id: '', name: '', manufacturer: '' };
  const id = p.id || p.code || p.sku || p.number || p.productCode || '';
  return {
    id,
    name: p.name || p.title || id || '',
    manufacturer: p.manufacturer || p.vendor || p.brand || ''
  };
}

// ============================================================================
// LAZY LOADING API FUNCTIONS (v2)
// These functions use the new backend endpoints for on-demand data loading
// instead of fetching all cell data upfront.
// ============================================================================

/**
 * Fetch product metadata (filter options, statistics, averages)
 * This is the primary call for initializing the explorer - replaces loading all cells!
 *
 * @param {object} overrides - Config overrides (token, url)
 * @returns {Promise<ProductMetadataResponse>} Metadata with filters, stats, averages
 *
 * Response structure:
 * {
 *   total_products: number,
 *   demo_products: number,
 *   filters: {
 *     manufacturers: [{ name: string, count: number }],
 *     formats: [...],
 *     technologies: [...],
 *     cathode_materials: [...],
 *     anode_materials: [...],
 *     countries: [...]
 *   },
 *   ranges: {
 *     W_C10: { abs: { min, max, avg, median, count, unit }, grav: {...}, vol: {...} },
 *     P_peak: {...},
 *     C_C10: {...},
 *     I_peak: {...},
 *     mass: {...},
 *     volume: {...},
 *     year: { min, max, count }
 *   },
 *   averages: {
 *     abs: { energy_wh, power_w, capacity_ah, current_a, mass_g, volume_cm3 },
 *     grav: { energy_wh_kg, power_w_kg, capacity_ah_kg, current_a_kg },
 *     vol: { energy_wh_l, power_w_l, capacity_ah_l, current_a_l }
 *   },
 *   cached_at: string (ISO timestamp)
 * }
 */
export async function getProductMetadata(overrides = {}) {
  const cfg = window.BATEMO_API?.list || {};
  const baseUrl = cfg.url || cfg.baseUrl || '';

  // Build metadata URL from list URL - add cache buster to force fresh data
  // e.g., /api/v1/products → /api/v1/products/metadata?_v=2
  const url = overrides.url || `${baseUrl.replace(/\/?$/, '')}/metadata?_v=2`;

  const token = overrides.token ?? cfg.token ?? null;
  const tokenParam = overrides.tokenParam ?? cfg.tokenParam ?? null;
  const headerName = overrides.headerName ?? cfg.headerName ?? null;
  const headerPrefix = overrides.headerPrefix ?? cfg.headerPrefix ?? null;

  if (!url) throw new Error('getProductMetadata: no URL configured');

  console.log('[dataService] 📊 Fetching product metadata (single call for filter options + stats)');

  const result = await apiGet(url, {
    token,
    tokenParam,
    headerName,
    headerPrefix,
    timeout: overrides.timeout || 30000,
    cache: false // TEMPORARY: Disable cache for metadata to get fresh chemistry filters
  });

  console.log(`[dataService] ✓ Metadata loaded: ${result.total_products} products, ${Object.keys(result.filters || {}).length} filter types`);

  return result;
}

/**
 * Fetch paginated products for explorer grid/table view
 *
 * @param {object} options - Query options
 * @param {number} options.page - Page number (1-based, default: 1)
 * @param {number} options.limit - Items per page (default: 50, max: 100)
 * @param {string} options.sort_by - Sort field (default: 'name')
 * @param {string} options.sort_order - Sort direction ('asc' or 'desc', default: 'asc')
 * @param {string} options.search - Search query (searches name, manufacturer)
 * @param {string} options.basis - Unit basis ('abs', 'grav', 'vol', default: 'abs')
 * @param {string[]} options.manufacturers - Filter by manufacturers
 * @param {string[]} options.formats - Filter by formats
 * @param {string[]} options.technologies - Filter by technologies
 * @param {number} options.energy_min - Min energy filter
 * @param {number} options.energy_max - Max energy filter
 * @param {number} options.power_min - Min power filter
 * @param {number} options.power_max - Max power filter
 * @param {object} overrides - Config overrides
 * @returns {Promise<PaginatedExplorerResponse>} Paginated product list
 *
 * Response structure:
 * {
 *   items: [{ product_id, name, manufacturer, format, technology, country, year,
 *             energy_abs, energy_grav, energy_vol, power_abs, power_grav, power_vol,
 *             capacity_abs, capacity_grav, capacity_vol, current_abs, current_grav, current_vol,
 *             mass_kg, volume_m3, CAP_spread_std_rel, CAP_loss_rel }],
 *   total: number,
 *   page: number,
 *   limit: number,
 *   pages: number,
 *   has_next: boolean,
 *   has_prev: boolean
 * }
 */
export async function getExplorerProducts(options = {}, overrides = {}) {
  const cfg = window.BATEMO_API?.list || {};
  const baseUrl = cfg.url || cfg.baseUrl || '';

  // Build explorer URL
  // e.g., /api/v1/products → /api/v1/products/explorer/v2
  const url = overrides.url || `${baseUrl.replace(/\/?$/, '')}/explorer/v2`;

  const token = overrides.token ?? cfg.token ?? null;
  const tokenParam = overrides.tokenParam ?? cfg.tokenParam ?? null;
  const headerName = overrides.headerName ?? cfg.headerName ?? null;
  const headerPrefix = overrides.headerPrefix ?? cfg.headerPrefix ?? null;

  if (!url) throw new Error('getExplorerProducts: no URL configured');

  // Build query parameters
  const query = {};

  // Pagination - backend expects 'per_page' not 'limit'
  if (options.page) query.page = options.page;
  if (options.limit) query.per_page = Math.min(options.limit, 50); // Backend max is 50

  // Sorting
  if (options.sort_by) query.sort_by = options.sort_by;
  if (options.sort_order) query.sort_order = options.sort_order;

  // Search
  if (options.search) query.search = options.search;

  // Basis (for unit conversion)
  if (options.basis) query.basis = options.basis;

  // Array filters (join with comma)
  if (options.manufacturers?.length) query.manufacturers = options.manufacturers.join(',');
  if (options.formats?.length) query.formats = options.formats.join(',');
  if (options.technologies?.length) query.technologies = options.technologies.join(',');
  if (options.cathode_materials?.length) query.cathode_materials = options.cathode_materials.join(',');
  if (options.anode_materials?.length) query.anode_materials = options.anode_materials.join(',');
  if (options.countries?.length) query.countries = options.countries.join(',');

  // Range filters
  if (options.energy_min != null) query.energy_min = options.energy_min;
  if (options.energy_max != null) query.energy_max = options.energy_max;
  if (options.power_min != null) query.power_min = options.power_min;
  if (options.power_max != null) query.power_max = options.power_max;
  if (options.capacity_min != null) query.capacity_min = options.capacity_min;
  if (options.capacity_max != null) query.capacity_max = options.capacity_max;
  if (options.current_min != null) query.current_min = options.current_min;
  if (options.current_max != null) query.current_max = options.current_max;
  if (options.mass_min != null) query.mass_min = options.mass_min;
  if (options.mass_max != null) query.mass_max = options.mass_max;
  if (options.year_min != null) query.year_min = options.year_min;
  if (options.year_max != null) query.year_max = options.year_max;

  console.log(`[dataService] 📋 Fetching explorer products (page ${options.page || 1}, limit ${options.limit || 50})`);

  const result = await apiGet(url, {
    token,
    tokenParam,
    headerName,
    headerPrefix,
    query,
    timeout: overrides.timeout || 30000,
    maxAge: CACHE_DURATION.DEFAULT, // Short cache for dynamic queries
    cache: false // Don't cache paginated results with filters
  });

  // Normalize response: backend returns 'products', frontend expects 'items'
  // Also normalize 'total_pages' to 'pages'
  const normalizedResult = {
    items: result.products || result.items || [],
    total: result.total || 0,
    page: result.page || 1,
    pages: result.total_pages || result.pages || 1,
    per_page: result.per_page || result.limit || options.limit || 50,
    has_next: result.has_next ?? (result.page < (result.total_pages || result.pages || 1)),
    has_prev: result.has_prev ?? (result.page > 1),
    thumbnail_expires_at: result.thumbnail_expires_at
  };

  console.log(`[dataService] ✓ Explorer loaded: ${normalizedResult.items.length} items (page ${normalizedResult.page}/${normalizedResult.pages}, total: ${normalizedResult.total})`);

  return normalizedResult;
}

/**
 * Fetch KPI data for scatter plots (Score Plot, My Plot)
 * Returns only the minimal data needed for plotting: id, name, manufacturer + KPI values
 *
 * @param {object} options - Query options
 * @param {string} options.basis - Unit basis ('abs', 'grav', 'vol', default: 'abs')
 * @param {string[]} options.product_ids - Optional: fetch specific products only
 * @param {object} overrides - Config overrides
 * @returns {Promise<ProductKPIsResponse>} KPI data for all products
 *
 * Response structure:
 * {
 *   items: [{
 *     product_id, name, manufacturer, is_demo,
 *     energy, power, capacity, current,
 *     CAP_spread_std_rel, CAP_loss_rel
 *   }],
 *   basis: string,
 *   total: number
 * }
 */
export async function getProductKpis(options = {}, overrides = {}) {
  const cfg = window.BATEMO_API?.list || {};
  const baseUrl = cfg.url || cfg.baseUrl || '';

  // Build KPIs URL
  // e.g., /api/v1/products → /api/v1/products/kpis
  const url = overrides.url || `${baseUrl.replace(/\/?$/, '')}/kpis`;

  const token = overrides.token ?? cfg.token ?? null;
  const tokenParam = overrides.tokenParam ?? cfg.tokenParam ?? null;
  const headerName = overrides.headerName ?? cfg.headerName ?? null;
  const headerPrefix = overrides.headerPrefix ?? cfg.headerPrefix ?? null;

  if (!url) throw new Error('getProductKpis: no URL configured');

  // Build query parameters
  const query = {};
  if (options.basis) query.basis = options.basis;
  if (options.product_ids?.length) query.product_ids = options.product_ids.join(',');

  console.log(`[dataService] 📈 Fetching KPI data for scatter plots (basis: ${options.basis || 'abs'})`);

  const result = await apiGet(url, {
    token,
    tokenParam,
    headerName,
    headerPrefix,
    query,
    timeout: overrides.timeout || 30000,
    maxAge: CACHE_DURATION.CELL_DATA // 24h cache
  });

  console.log(`[dataService] ✓ KPIs loaded: ${result.items?.length || 0} products (basis: ${result.basis})`);

  return result;
}

/**
 * Fetch specific categories for a cell (lazy section loading)
 * Use this for CellPage to load sections on-demand instead of all data upfront.
 *
 * @param {string} cellId - Cell product ID
 * @param {string[]} categories - Categories to fetch (e.g., ['electrical', 'mechanical'])
 * @param {object} overrides - Config overrides
 * @returns {Promise<object>} Normalized cell data with only requested categories
 *
 * Available categories:
 * - 'general': Basic info (name, manufacturer, country, etc.)
 * - 'electrical': Voltage, capacity, power, current, resistance, efficiency
 * - 'mechanical': Dimensions, mass, volume, format
 * - 'chemical': Cathode, anode, housing materials
 * - 'thermal': Temperature, power loss, efficiency at conditions
 * - 'safety': Safety features (ISP, PTC, CID, BURST, VENT)
 * - 'aging': Capacity loss, resistance increase over time
 * - 'batch': Production spread statistics
 */
export async function getCellForSection(cellId, categories = ['general'], overrides = {}) {
  if (!cellId) throw new Error('getCellForSection: cellId required');
  if (!Array.isArray(categories) || categories.length === 0) {
    categories = ['general'];
  }

  console.log(`[dataService] 🔍 Fetching cell ${cellId} sections: [${categories.join(', ')}]`);

  // Use existing getCellNormalized with categories override
  const result = await getCellNormalized(cellId, {
    ...overrides,
    categories: categories
  });

  console.log(`[dataService] ✓ Cell sections loaded for ${cellId}: [${categories.join(', ')}]`);

  return result;
}

/**
 * @deprecated Use getProductMetadata() instead
 * This function loads ALL cell data which is slow and wastes bandwidth.
 * The new /metadata endpoint provides filter options and stats in a single efficient call.
 */
export async function getGlobalStatsFromCells(cells) {
  console.warn('[dataService] ⚠️ getGlobalStatsFromCells is DEPRECATED. Use getProductMetadata() instead.');
  // Keep implementation for backward compatibility during migration
  if (!cells || !Array.isArray(cells)) return null;

  const stats = {
    totalProducts: cells.length,
    manufacturers: [...new Set(cells.map(c => c.manufacturer).filter(Boolean))],
    formats: [...new Set(cells.map(c => c.format || c.mechanical?.format).filter(Boolean))],
    technologies: [...new Set(cells.map(c => c.technology).filter(Boolean))]
  };

  return stats;
}


/**
 * Look up a cell from the explorer cache (allCells signal) by ID.
 * Returns null if not found.
 *
 * @param {string} cellId - Product ID to look up
 * @returns {object|null} Cell object from explorer data, or null
 */
export function getCellFromExplorerCache(cellId) {
  const cells = allCells.value;
  if (!cells || cells.length === 0) return null;
  return cells.find(c => c.id === cellId || c.product_id === cellId) || null;
}


/**
 * Fetch curve array data for multiple products in a single request.
 * Uses the batch-curves endpoint to avoid N individual product fetches.
 *
 * Results are cached in IndexedDB for 24 hours keyed by sorted product_ids + curve_types.
 *
 * Unit conversions applied (same as getCellNormalized):
 *   - DCC/CHC energy arrays: J → Wh (÷3600)
 *   - DCC/CHC capacity arrays: C → Ah (÷3600)
 *   - Temperatures: K → °C (−273.15)
 *   - EIS impedance: Ω → mΩ (×1000)
 *   - DCP/CHP resistance: Ω → mΩ (×1000)
 *
 * @param {string[]} productIds - Product IDs (max 10 per request)
 * @param {string[]} curveTypes - Curve types (e.g. ['dcc'], max 3)
 * @param {object} [options] - Options
 * @param {string} [options.baseUrl] - Override API base URL
 * @returns {Promise<{cells: object, metadata: object}>}
 */
export async function getBatchCurves(productIds, curveTypes = ['dcc'], options = {}) {
  const cfg = window.BATEMO_API?.list || {};
  const baseUrl = options.baseUrl || cfg.url || cfg.baseUrl || '';
  const apiBase = baseUrl.replace(/\/?$/, '');
  const url = `${apiBase}/batch-curves`;

  // Cache key: sorted ids + sorted types for deterministic key
  const sortedIds = [...productIds].sort().join(',');
  const sortedTypes = [...curveTypes].sort().join(',');
  const cacheKey = `batch-curves:${sortedIds}:${sortedTypes}`;

  // Check IndexedDB cache
  try {
    const cached = await getIndexedDBCache(cacheKey);
    if (cached?.data) {
      const age = Date.now() - (cached.ts || 0);
      if (age < CACHE_DURATION.CELL_DATA) {
        console.log(`[dataService] ✓ Batch curves cache hit (${Math.round(age / 1000)}s old)`);
        return cached.data;
      }
    }
  } catch (e) {
    console.warn('[dataService] Batch curves IndexedDB read error:', e);
  }

  console.log(`[dataService] 📦 Fetching batch curves: ${productIds.length} cells, types=[${curveTypes}]`);

  const jwtToken = getAccessToken();
  const headers = {
    'Content-Type': 'application/json',
    'X-Request-Source': 'batch-curves'
  };
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ product_ids: productIds, curve_types: curveTypes })
  });

  if (!res.ok) {
    throw new Error(`Batch curves request failed: ${res.status}`);
  }

  assertJsonResponse(res, url);
  const data = await res.json();

  // Apply unit conversions to curve arrays (SI → display units)
  const converted = convertBatchCurveUnits(data);

  // Cache in IndexedDB
  try {
    await setIndexedDBCache(cacheKey, { data: converted, ts: Date.now() });
  } catch (e) {
    console.warn('[dataService] Batch curves IndexedDB write error:', e);
  }

  console.log(`[dataService] ✓ Batch curves loaded for ${Object.keys(converted.cells).length} cells`);
  return converted;
}


/**
 * Convert batch-curves response from SI units to display units.
 * Mirrors the conversions in getCellNormalized().
 */
function convertBatchCurveUnits(data) {
  const mapArray = (arr, fn) => Array.isArray(arr) ? arr.map(v => v != null ? fn(v) : null) : arr;

  const result = { cells: {}, metadata: data.metadata };

  for (const [productId, curves] of Object.entries(data.cells)) {
    const converted = {};

    for (const [curveType, fields] of Object.entries(curves)) {
      const out = { ...fields };

      if (curveType === 'dcc' || curveType === 'chc') {
        // Energy: J → Wh
        const wKey = curveType === 'dcc' ? 'W_DCC' : 'W_CHC';
        if (out[wKey]) out[wKey] = mapArray(out[wKey], v => v / 3600);
        // Capacity: C → Ah
        const cKey = curveType === 'dcc' ? 'C_DCC' : 'C_CHC';
        if (out[cKey]) out[cKey] = mapArray(out[cKey], v => v / 3600);
        // Temperature: K → °C
        const tKey = curveType === 'dcc' ? 'T_amb_DCC' : 'T_amb_CHC';
        if (out[tKey]) out[tKey] = mapArray(out[tKey], v => v - 273.15);
      }

      if (curveType === 'dcp' || curveType === 'chp') {
        // Resistance: Ω → mΩ
        const rKey = curveType === 'dcp' ? 'R_10_DCP' : 'R_10_CHP';
        if (out[rKey]) out[rKey] = mapArray(out[rKey], v => v * 1000);
        // Temperature: K → °C
        const tKey = curveType === 'dcp' ? 'T_amb_DCP' : 'T_amb_CHP';
        if (out[tKey]) out[tKey] = mapArray(out[tKey], v => v - 273.15);
      }

      if (curveType === 'eis') {
        // Impedance: Ω → mΩ
        if (out.EIS_25_Z_real) out.EIS_25_Z_real = mapArray(out.EIS_25_Z_real, v => v * 1000);
        if (out.EIS_25_Z_imag) out.EIS_25_Z_imag = mapArray(out.EIS_25_Z_imag, v => v * 1000);
      }

      if (curveType === 'thermal') {
        // Temperature arrays: K → °C
        if (out.T_max_DCC) out.T_max_DCC = mapArray(out.T_max_DCC, v => v - 273.15);
        if (out.T_max_CHC) out.T_max_CHC = mapArray(out.T_max_CHC, v => v - 273.15);
        // Energy loss: J → Wh
        if (out.W_loss_DCC) out.W_loss_DCC = mapArray(out.W_loss_DCC, v => v / 3600);
        if (out.W_loss_CHC) out.W_loss_CHC = mapArray(out.W_loss_CHC, v => v / 3600);
      }

      if (curveType === 'batch_spread') {
        // Capacity: C → Ah
        if (out.CAP_spread) out.CAP_spread = mapArray(out.CAP_spread, v => v / 3600);
        if (out.CAP_spread_mean != null) out.CAP_spread_mean = out.CAP_spread_mean / 3600;
        if (out.CAP_spread_std_abs != null) out.CAP_spread_std_abs = out.CAP_spread_std_abs / 3600;
        // EIS: Ω → mΩ
        if (out.EIS_spread) out.EIS_spread = mapArray(out.EIS_spread, v => v * 1000);
        if (out.EIS_spread_mean != null) out.EIS_spread_mean = out.EIS_spread_mean * 1000;
        if (out.EIS_spread_std_abs != null) out.EIS_spread_std_abs = out.EIS_spread_std_abs * 1000;
        // Relative values stay as-is (fractions)
      }

      if (curveType === 'aging') {
        // Capacity loss: C → Ah
        if (out.CAP_loss != null) out.CAP_loss = out.CAP_loss / 3600;
        // EIS increase: Ω → mΩ
        if (out.EIS_increase != null) out.EIS_increase = out.EIS_increase * 1000;
        // Relative values stay as-is (fractions)
        // Aging curve arrays
        if (out.CAP_T) out.CAP_T = mapArray(out.CAP_T, v => v - 273.15);  // K → °C
        if (out.CAP_Qtp_0) out.CAP_Qtp_0 = mapArray(out.CAP_Qtp_0, v => v / 3600);  // C → Ah
        if (out.CAP_Qtp_standard) out.CAP_Qtp_standard = mapArray(out.CAP_Qtp_standard, v => v / 3600);
        if (out.EIS_T) out.EIS_T = mapArray(out.EIS_T, v => v - 273.15);  // K → °C
        if (out.EIS_Qtp_0) out.EIS_Qtp_0 = mapArray(out.EIS_Qtp_0, v => v * 1000);  // Ω → mΩ
        if (out.EIS_Qtp_standard) out.EIS_Qtp_standard = mapArray(out.EIS_Qtp_standard, v => v * 1000);
      }

      converted[curveType] = out;
    }

    result.cells[productId] = converted;
  }

  return result;
}
